import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Download, Clock, AlertCircle, Loader, BookOpen, Plus, X, Users, Film, Send, Trash2, Cpu, Upload } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { useAuth } from '../context/AuthContext'
import DashboardLayout from '../components/DashboardLayout'
import SendCardsModal from '../components/SendCardsModal'
import BuyAnalysesModal from '../components/BuyAnalysesModal'
import { hasStudioAccess, estVraiFree } from '../utils/planAccess'
import { resolveLineupPlayers } from '../utils/resolveLineupPlayers'
import { isHomeMatch, scoresUsThem, scoresUsThemHT, toRawScores, isWin, isDefeat } from '../utils/matchResult'
import { T, globalStyles } from '../theme'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import matchService from '../services/matchService'
import KitColorConfirm, { couleursCompletes } from '../components/KitColorConfirm'
import VideoDropSlots from '../components/VideoDropSlots'
import VideoScopeChoice, { reperesAttendus } from '../components/VideoScopeChoice'
import { useKitColorDefaults } from '../hooks/useKitColorDefaults'
import { couleurNousParDefaut } from '../utils/kitColors'
import { uploadFileMultipart, verifierFichierVideo } from '../services/multipartUpload'
import MatchAnalyseReport from '../components/MatchAnalyseReport'
import { analyseResultToLegacyStats } from '../utils/analyseResultMapper'

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

const D     = '#1a1916'
const GOLD  = '#4F8EF7'
const GOLD_BG  = 'rgba(79,142,247,0.08)'
const GOLD_BDR = 'rgba(79,142,247,0.25)'
const CREAM = '#f5f2eb'
const INK   = '#1a1916'
const MUTED = 'rgba(26,25,22,0.45)'
const RULE  = 'rgba(26,25,22,0.09)'
const GREEN = '#22c55e'
const RED   = '#ef4444'
const BLUE  = '#3b82f6'
const ORANGE = '#f59e0b'
const MONO  = T.mono
const DISP  = T.display

const TIMELINE_MARKERS = [
  { key: 'kickOff1',  label: 'Coup d\'envoi MT1', color: '#4ade80' },
  { key: 'halfTime',  label: 'Fin de la MT1',     color: '#f97316' },
  { key: 'kickOff2',  label: 'Coup d\'envoi MT2', color: '#60a5fa' },
  { key: 'fullTime',  label: 'Fin du match',       color: '#f43f5e' },
]

// MODALE-TIMELINE-SCOPE-001 · sur une video d'UNE SEULE periode, « Fin de la
// MT1 » et « Coup d'envoi MT2 » N'EXISTENT PAS. Les exiger laissait le bouton
// gris pour toujours · c'est le mur corrige dans le wizard le 11/08, et cette
// modale est un chemin distinct qui n'avait pas ete aligne.
//
// Memes CLEFS que ci-dessus (`kickOff1` / `fullTime`) · elles portent deja
// « debut » et « fin », et les reutiliser laisse `halfTime` et `kickOff2` a
// NULL. Ce n'est pas cosmetique · les QUATRE reperes non nuls sont ce que
// `recovery.py:142` exige pour relancer une decoupe, donc en laisser deux a
// NULL est ce qui protege ce match d'un decoupage qu'il n'attend pas.
const MARQUEURS_UNE_PERIODE = [
  { key: 'kickOff1', label: 'Début de la période', color: '#4ade80' },
  { key: 'fullTime', label: 'Fin de la période',   color: '#f43f5e' },
]

// La DECISION vit dans `reperesAttendus` (VideoScopeChoice.jsx), partagee avec
// le wizard · deux lectures du meme fait divergeraient au premier correctif
// applique d'un seul cote. Ici on ne choisit que la liste correspondante ·
// 0 → aucune (deux fichiers), 2 → debut/fin, 4 → les quatre.
function marqueursPour(scope) {
  const n = reperesAttendus(scope)
  if (n === 0) return []
  return n === 2 ? MARQUEURS_UNE_PERIODE : TIMELINE_MARKERS
}

// P1-SPLIT-MATCHDETAIL-001 · cadence du suivi de decoupe.
// Meme cadence que le wizard (CreateMatch.jsx), pour la meme raison : le
// timeout par requete, plus court que l'intervalle, empeche une requete restee
// pendante de suspendre le suivi jusqu'a la fin des temps. C'est ce defaut qui
// a fige l'ecran du 09/08 alors que les deux mi-temps etaient deja en base.
const DECOUPE_POLL_MS        = 4000
const DECOUPE_REQ_TIMEOUT_MS = 15000
// Au-dela, on cesse de poller et on le dit. La decoupe, elle, continue cote
// serveur : recharger la page rebranche le suivi sur l'etat reel.
const DECOUPE_ATTENTE_MAX_MS = 45 * 60 * 1000

// Les deux seuls statuts qui signent un traitement EN COURS.
//
// `pending` n'en fait deliberement pas partie : c'est un etat de REPOS, pose
// quand une source est uploadee sans marqueurs (routes/matches.py:342) ou quand
// le split est reporte faute de place disque (tasks/half_split.py:115). L'y
// inclure afficherait un bandeau « decoupe en cours » perpetuel sur un match ou
// rien ne tourne.
const DECOUPE_EN_COURS = new Set(['splitting', 'processing'])

function libelleDecoupe(status) {
  if (status === 'processing') return 'Traitement de la vidéo…'
  return 'Découpe MT1 / MT2 en cours…'
}

function fmtTime(secs) {
  if (secs == null) return '--:--'
  const s = Math.round(secs)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
}

const FORMATIONS = ['4-3-3','4-4-2','4-2-3-1','3-5-2','3-4-3','5-3-2','4-1-4-1','4-5-1','3-4-1-2']

const POS_COLOR = {
  GB: ORANGE,
  RB: BLUE, LB: BLUE, CB: BLUE, SW: BLUE,
  DM: '#6366f1', CM: '#6366f1', CAM: '#6366f1',
  RW: GREEN, LW: GREEN, ST: RED, CF: RED,
  Gardien: ORANGE, Défenseur: BLUE, Milieu: '#6366f1', Attaquant: RED,
}
const getPosColor = (pos) => POS_COLOR[pos] || MUTED

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

function StatusBadge({ status, progress }) {
  const map = {
    DRAFT:      { label: 'Carnet',     color: MUTED },
    PENDING:    { label: 'En attente', color: ORANGE },
    PROCESSING: { label: `${progress || 0}%`, color: BLUE },
    COMPLETED:  { label: 'Terminé',   color: GREEN },
    ANALYZED:   { label: 'Analysé',   color: GOLD },
    ERROR:      { label: 'Erreur',    color: RED },
    pending:    { label: 'En attente', color: ORANGE },
    completed:  { label: 'Terminé',   color: GREEN },
    processing: { label: `${progress || 0}%`, color: BLUE },
    error:      { label: 'Erreur',    color: RED },
  }
  const s = map[status] || { label: status, color: MUTED }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '4px 12px', background: s.color + '22', color: s.color, border: `1px solid ${s.color}40` }}>
      {s.label}
    </span>
  )
}

function Card({ children, accentColor, style = {} }) {
  return <div style={{ background: '#fff', border: `1px solid ${RULE}`, borderTop: `2px solid ${accentColor || GOLD_BDR}`, ...style }}>{children}</div>
}

function SectionTitle({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderBottom: `1px solid ${RULE}` }}>
      <span style={{ width: 14, height: 1, background: GOLD, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD }}>{children}</span>
    </div>
  )
}

function EventBadge({ type }) {
  const map = {
    goal:           { label: 'But',         color: RED },
    assist:         { label: 'Passe D.',     color: BLUE },
    yellow:         { label: 'Carton J.',    color: ORANGE },
    red:            { label: 'Carton R.',    color: RED },
    penalty_scored: { label: 'Penalty',      color: RED },
    penalty_missed: { label: 'Raté',         color: MUTED },
    own_goal:       { label: 'CSC',          color: MUTED },
  }
  const t = map[type] || { label: type, color: MUTED }
  return (
    <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', padding: '2px 8px', color: t.color, border: `1px solid ${t.color}35`, background: t.color + '12', flexShrink: 0 }}>
      {t.label}
    </span>
  )
}

// ── Modal saisie composition ────────────────────────────────────
function LineupModal({ matchId, category, formation: initFormation, onSave, onClose }) {
  const [players, setPlayers]     = useState([])
  const [formation, setFormation] = useState(initFormation || '4-3-3')
  const [roles, setRoles]         = useState({}) // player_id -> 'starter' | 'sub' | ''
  const [saving, setSaving]       = useState(false)
  const [loadingP, setLoadingP]   = useState(true)

  useEffect(() => {
    fetch(`${API}/players/`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const all = Array.isArray(data) ? data : []
        const filtered = category ? all.filter(p => p.category === category) : all
        setPlayers(filtered.sort((a, b) => (a.number || 99) - (b.number || 99)))
        setLoadingP(false)
      })
      .catch(() => setLoadingP(false))
  }, [])

  const starters = players.filter(p => roles[p.id] === 'starter')
  const subs     = players.filter(p => roles[p.id] === 'sub')

  const toggleRole = (id, newRole) => {
    setRoles(prev => ({ ...prev, [id]: prev[id] === newRole ? '' : newRole }))
  }

  const handleSave = async () => {
    setSaving(true)
    const lineup = [
      ...starters.map(p => ({ player_id: p.id, name: p.name, number: p.number, position: p.position, starter: true })),
      ...subs.map(p => ({ player_id: p.id, name: p.name, number: p.number, position: p.position, starter: false })),
    ]
    try {
      const res = await fetch(`${API}/matches/${matchId}/`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ lineup, formation }),
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      onSave(data)
    } catch { setSaving(false) }
  }

  const posOrder = ['Gardien', 'Défenseur', 'Milieu', 'Attaquant', 'GB']
  const playersByPos = posOrder.reduce((acc, pos) => {
    const grp = players.filter(p => p.position === pos || (pos === 'Gardien' && p.position === 'GB'))
    if (grp.length) acc.push({ pos: pos === 'GB' ? 'Gardien' : pos, players: grp })
    return acc
  }, [])
  const ungrouped = players.filter(p => !posOrder.includes(p.position))
  if (ungrouped.length) playersByPos.push({ pos: 'Autre', players: ungrouped })

  // Dédupliquer
  const seen = new Set()
  const groups = playersByPos.filter(g => {
    const key = g.pos
    if (seen.has(key)) return false
    seen.add(key); return true
  })

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(26,25,22,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: CREAM, width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', background: D, borderBottom: `1px solid rgba(245,242,235,0.08)` }}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.7)', marginBottom: 4 }}>Saisie composition</div>
            <div style={{ fontFamily: DISP, fontSize: 18, color: CREAM, textTransform: 'uppercase' }}>{starters.length} titulaires · {subs.length} remplaçants</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(245,242,235,0.5)', padding: 8 }}>
            <X size={18} />
          </button>
        </div>

        {/* Formation */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${RULE}`, display: 'flex', alignItems: 'center', gap: 12, background: '#fff' }}>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, flexShrink: 0 }}>Formation</span>
          <select value={formation} onChange={e => setFormation(e.target.value)} style={{ fontFamily: MONO, fontSize: 12, border: `1px solid ${RULE}`, background: CREAM, padding: '6px 12px', color: INK, cursor: 'pointer', outline: 'none' }}>
            {FORMATIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        {/* Liste joueurs */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loadingP ? (
            <div style={{ textAlign: 'center', padding: 40, fontFamily: MONO, fontSize: 10, color: MUTED }}>Chargement...</div>
          ) : groups.map(({ pos, players: grpPlayers }) => (
            <div key={pos}>
              <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: getPosColor(pos), padding: '10px 20px 6px', background: CREAM }}>
                {pos}
              </div>
              {grpPlayers.map(p => {
                const role = roles[p.id] || ''
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: `1px solid ${RULE}`, background: role ? '#fff' : 'transparent' }}>
                    <span style={{ fontFamily: DISP, fontSize: 16, color: GOLD, width: 24, flexShrink: 0 }}>{p.number}</span>
                    <span style={{ fontFamily: MONO, fontSize: 12, color: INK, flex: 1 }}>{p.name}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => toggleRole(p.id, 'starter')} style={{ padding: '5px 12px', fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', border: `1px solid ${role === 'starter' ? GREEN : RULE}`, background: role === 'starter' ? GREEN + '18' : 'transparent', color: role === 'starter' ? GREEN : MUTED, transition: 'all .1s' }}>
                        TIT
                      </button>
                      <button onClick={() => toggleRole(p.id, 'sub')} style={{ padding: '5px 12px', fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', border: `1px solid ${role === 'sub' ? BLUE : RULE}`, background: role === 'sub' ? BLUE + '18' : 'transparent', color: role === 'sub' ? BLUE : MUTED, transition: 'all .1s' }}>
                        RMP
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 20px', borderTop: `1px solid ${RULE}`, background: '#fff', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', background: 'transparent', border: `1px solid ${RULE}`, color: MUTED, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving || starters.length === 0} style={{ flex: 2, padding: '12px', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', background: starters.length > 0 ? D : RULE, color: starters.length > 0 ? CREAM : MUTED, border: 'none', cursor: starters.length > 0 ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
            {saving ? 'Enregistrement...' : `Enregistrer ${starters.length > 0 ? `(${starters.length} tit.)` : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Vue composition ─────────────────────────────────────────────
function LineupView({ lineup, events, formation, isMobile, onEdit }) {
  const starters = lineup.filter(p => p.starter)
  const subs     = lineup.filter(p => !p.starter)

  const evByPlayer = {}
  ;(events || []).forEach(ev => {
    if (!evByPlayer[ev.player_id]) evByPlayer[ev.player_id] = []
    evByPlayer[ev.player_id].push(ev)
  })

  const evIcon = (type) => {
    if (type === 'goal' || type === 'penalty_scored') return '⚽'
    if (type === 'assist') return '🎯'
    if (type === 'yellow') return '🟨'
    if (type === 'red') return '🟥'
    if (type === 'own_goal') return '⚽'
    return ''
  }

  return (
    <Card accentColor={GOLD_BDR}>
      {/* Header avec formation et bouton modifier */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${RULE}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 14, height: 1, background: GOLD, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD }}>Composition</span>
          {formation && (
            <span style={{ fontFamily: DISP, fontSize: 20, color: INK, letterSpacing: '.04em' }}>{formation}</span>
          )}
        </div>
        {onEdit && (
          <button onClick={onEdit} style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', padding: '5px 12px', background: 'transparent', border: `1px solid ${RULE}`, color: MUTED, cursor: 'pointer' }}>
            Modifier
          </button>
        )}
      </div>

      {/* Titulaires */}
      <div style={{ padding: '0' }}>
        <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, padding: '10px 20px 6px', background: CREAM }}>
          Titulaires — {starters.length}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: 0 }}>
          {starters.map((p, i) => {
            const playerEvents = evByPlayer[p.player_id] || []
            const posColor = getPosColor(p.position)
            return (
              <div key={p.player_id || i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px',
                borderBottom: `1px solid ${RULE}`,
                borderRight: (i % (isMobile ? 2 : 3)) < (isMobile ? 1 : 2) ? `1px solid ${RULE}` : 'none',
              }}>
                {/* Numéro */}
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: posColor + '15', border: `1px solid ${posColor}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: DISP, fontSize: 14, color: posColor }}>{p.number}</span>
                </div>
                {/* Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', color: posColor, marginTop: 2 }}>{p.position}</div>
                </div>
                {/* Événements */}
                {playerEvents.length > 0 && (
                  <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                    {playerEvents.map((ev, j) => (
                      <span key={j} style={{ fontSize: 12 }} title={`${ev.event_type}${ev.minute ? ` ${ev.minute}'` : ''}`}>
                        {evIcon(ev.event_type)}
                      </span>
                    ))}
                  </div>
                )}
                {/* Sortant */}
                {p.subbed_off && (
                  <span style={{ fontFamily: MONO, fontSize: 8, color: RED, flexShrink: 0 }}>↓{p.subbed_off}'</span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Remplaçants */}
      {subs.length > 0 && (
        <div>
          <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, padding: '10px 20px 6px', background: CREAM, borderTop: `1px solid ${RULE}` }}>
            Remplaçants — {subs.length}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
            {subs.map((p, i) => {
              const playerEvents = evByPlayer[p.player_id] || []
              return (
                <div key={p.player_id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: `1px solid ${RULE}`, borderRight: `1px solid ${RULE}`, minWidth: isMobile ? '50%' : '33%', flex: '0 0 auto', boxSizing: 'border-box' }}>
                  <span style={{ fontFamily: DISP, fontSize: 14, color: MUTED, width: 22, flexShrink: 0 }}>{p.number}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    {p.subbed_in && <span style={{ fontFamily: MONO, fontSize: 8, color: GREEN }}>↑{p.subbed_in}'</span>}
                  </div>
                  {playerEvents.map((ev, j) => <span key={j} style={{ fontSize: 12 }}>{evIcon(ev.event_type)}</span>)}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Card>
  )
}


// ── Modal ajout événement ────────────────────────────────────────
function EventModal({ matchId, category, onSave, onClose }) {
  const [players, setPlayers]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState(null)
  const [eventType, setEventType] = useState('goal')
  const [playerId, setPlayerId]   = useState('')
  const [minute, setMinute]       = useState('')

  const EVENT_TYPES = [
    { id: 'goal',           label: 'But' },
    { id: 'assist',         label: 'Passe D.' },
    { id: 'yellow',         label: 'Carton J.' },
    { id: 'red',            label: 'Carton R.' },
    { id: 'penalty_scored', label: 'Penalty' },
    { id: 'penalty_missed', label: 'Penalty raté' },
    { id: 'own_goal',       label: 'CSC' },
  ]

  useEffect(() => {
    fetch(`${API}/players/`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const all = Array.isArray(data) ? data : []
        const filtered = category ? all.filter(p => p.category === category) : all
        setPlayers(filtered.sort((a, b) => (a.number || 99) - (b.number || 99)))
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    if (!playerId) { setError('Sélectionne un joueur'); return }
    setSaving(true)
    try {
      const res = await fetch(`${API}/matches/${matchId}/events/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          event_type: eventType,
          player_id: playerId,
          minute: minute ? parseInt(minute) : null,
        }),
      })
      if (!res.ok) {
        const d = await res.json()
        throw new Error(typeof d.detail === 'string' ? d.detail : 'Erreur')
      }
      const ev = await res.json()
      onSave(ev)
    } catch (e) {
      setError(e.message)
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(26,25,22,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: CREAM, width: '100%', maxWidth: 520, boxShadow: '0 -8px 40px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: D }}>
          <span style={{ fontFamily: DISP, fontSize: 16, color: CREAM, textTransform: 'uppercase' }}>Ajouter un événement</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(245,242,235,0.5)', padding: 8 }}><X size={16} /></button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Type événement */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, display: 'block', marginBottom: 8 }}>Type</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {EVENT_TYPES.map(et => (
                <button key={et.id} onClick={() => setEventType(et.id)} style={{
                  padding: '6px 12px', fontFamily: MONO, fontSize: 9, letterSpacing: '.08em',
                  cursor: 'pointer', border: `1px solid ${eventType === et.id ? GOLD : RULE}`,
                  background: eventType === et.id ? GOLD_BG : 'transparent',
                  color: eventType === et.id ? GOLD : MUTED,
                }}>
                  {et.label}
                </button>
              ))}
            </div>
          </div>

          {/* Joueur */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, display: 'block', marginBottom: 8 }}>Joueur</label>
            {loading ? (
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>Chargement...</div>
            ) : (
              <select value={playerId} onChange={e => setPlayerId(e.target.value)}
                style={{ width: '100%', fontFamily: MONO, fontSize: 12, border: `1px solid ${RULE}`, background: '#fff', padding: '9px 12px', color: playerId ? INK : MUTED, outline: 'none', cursor: 'pointer' }}>
                <option value="">-- Sélectionner --</option>
                {players.map(p => (
                  <option key={p.id} value={p.id}>{p.number ? `${p.number}. ` : ''}{p.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Minute */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, display: 'block', marginBottom: 8 }}>Minute (optionnel)</label>
            <input type="number" min="1" max="130" value={minute}
              onChange={e => setMinute(e.target.value)} placeholder="ex. 67"
              style={{ width: '100%', fontFamily: MONO, fontSize: 12, border: `1px solid ${RULE}`, background: '#fff', padding: '9px 12px', color: INK, outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = GOLD}
              onBlur={e => e.target.style.borderColor = RULE}
            />
          </div>

          {error && <div style={{ fontFamily: MONO, fontSize: 10, color: RED, marginBottom: 12 }}>{error}</div>}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '11px', fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', background: 'transparent', border: `1px solid ${RULE}`, color: MUTED, cursor: 'pointer' }}>
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving || !playerId} style={{ flex: 2, padding: '11px', fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', background: playerId ? D : RULE, color: playerId ? CREAM : MUTED, border: 'none', cursor: playerId ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
              {saving ? 'Enregistrement...' : 'Ajouter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Page principale ─────────────────────────────────────────────

// ── BANDEAU DU MATCH · styles et sous-composants ──────────────────────────
// Tout vient de `theme.js`, prefixe `mh`. Aucune valeur en dur ici.

const BTN_BANDEAU = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
  background: 'transparent', border: `1px solid ${T.mhRule}`, color: T.mhMuted,
  fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
  cursor: 'pointer', whiteSpace: 'nowrap',
}

const INPUT_SCORE = {
  fontFamily: T.display, fontSize: 40, lineHeight: 1, width: 74, textAlign: 'center',
  background: 'transparent', border: `1px solid ${T.mhRule}`, color: T.mhInk,
  outline: 'none', padding: '2px 0',
}

const INPUT_SCORE_HT = {
  ...INPUT_SCORE, fontSize: 18, width: 46,
}

// Le nom de l'equipe, au-dessus de son champ. Deux chiffres nus entre deux
// noms d'equipe ne disent pas lequel va ou : c'est ce qui a fait saisir un
// score a l'envers (P0-SCORE-ECRITURE-001). Le libelle n'est pas decoratif,
// il est la seule chose qui rattache un chiffre a une equipe.
const LBL_EQUIPE = {
  fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase',
  color: T.mhMuted, marginBottom: 5, textAlign: 'center',
  maxWidth: 88, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const FEELINGS = [
  { id: 'satisfait', label: '✓ Satisfait' },
  { id: 'mitige',    label: '~ Mitigé' },
  { id: 'difficile', label: '✗ Difficile' },
]
const feelingLabel = (f) => (FEELINGS.find(x => x.id === f) || {}).label || f

// NOM D'EQUIPE · taille adaptative puis troncature.
//
// Le nom le plus long en base fait 25 caracteres, mais rien ne l'y contraint :
// la mise en page doit tenir a 40. Deux paliers de police avant la troncature,
// et le nom complet reste lisible au survol par `title`. Jamais de debordement,
// jamais de police illisible.
function EquipeNom({ nom, lieu, align, isMobile }) {
  const n = (nom || '').trim()
  const taille = isMobile ? 18 : n.length > 28 ? 17 : n.length > 18 ? 21 : 26
  return (
    <div style={{ textAlign: align, minWidth: 0 }}>
      <div title={n} style={{
        fontFamily: T.display, fontSize: taille, fontWeight: 700, lineHeight: 1.1,
        textTransform: 'uppercase', letterSpacing: '.01em', color: T.mhInk,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {n || '—'}
      </div>
      <div style={{
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em',
        textTransform: 'uppercase', color: T.mhMuted, marginTop: 4,
      }}>
        {lieu}
      </div>
    </div>
  )
}

function ContexteItem({ label, valeur }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
      <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.mhUs, flexShrink: 0 }}>{label}</span>
      <span title={valeur} style={{ fontFamily: T.mono, fontSize: 11, color: T.mhInk, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{valeur}</span>
    </div>
  )
}

// VERSION DE FORMULE LA PLUS BASSE parmi les indicateurs presents.
//
// Une impression ou un PDF FIGE les valeurs. Si une formule change ensuite, le
// document garde l'ancienne sans que rien ne le dise, et il est deja chez le
// coach. Le numero imprime permet de retrouver les documents contenant au
// moins un indicateur d'ancienne formule — d'ou la plus BASSE et non la plus
// haute : c'est le maillon faible qu'on veut pouvoir tracer.
function versionFormuleLaPlusBasse(resultats) {
  const versions = []
  for (const src of [resultats?.full, resultats?.mt1, resultats?.mt2]) {
    const bloc = src?.indicateurs_derives
    if (!bloc || typeof bloc !== 'object') continue
    for (const ind of Object.values(bloc)) {
      if (typeof ind?.formule_version === 'number') versions.push(ind.formule_version)
    }
  }
  return versions.length ? Math.min(...versions) : null
}

// ONGLETS ADRESSABLES PAR URL.
//
// Deux raisons · un coach peut envoyer un lien direct vers le rapport, et le
// Chromium qui produit le PDF a besoin d'ouvrir cet onglet sans cliquer sur un
// selecteur qui bougera au prochain remaniement.
//
// Les slugs sont LISIBLES et stables ; les identifiants internes (`ia`,
// `overview`) ne sortent pas dans l'URL — ils peuvent changer sans casser un
// lien deja envoye.
const SLUGS_ONGLETS = {
  apercu: 'overview',
  joueurs: 'players',
  rapport: 'ia',
  insights: 'insights',
}
const ONGLET_VERS_SLUG = Object.fromEntries(
  Object.entries(SLUGS_ONGLETS).map(([slug, id]) => [id, slug])
)

// SOUS-ONGLET DU RAPPORT · `?tab=rapport&periode=mt1`.
// Sans `periode`, le rapport ouvre le match complet — c'est son repli naturel,
// pose dans MatchAnalyseReport. Ce parametre ne fait que le surcharger.
const SLUGS_PERIODE = { complet: 'full', mt1: 'mt1', mt2: 'mt2' }

// ── CE QU'UNE RELANCE VA REFAIRE, ET CE QU'ELLE GARDE ──────────────────────
//
// Fonction pure, hors composant · elle est appelee apres des retours
// anticipes, donc elle ne peut pas etre un hook.
//
// La liste `analysis_relance_prevue` est calculee par le SERVEUR · lui seul
// sait si une mi-temps a vraiment depose son resultat sur S3. La recalculer
// ici a partir des statuts donnerait une phrase fausse le jour ou un
// `succeeded` n'aurait pas de fichier derriere.
//
// Copie positive · on nomme ce qui repart et ce qui est conserve, jamais ce
// qui a echoue. Le bouton disait « Relancer l'analyse » sans preciser sur
// quoi ; le 08/08/2026 un coach dont la 2e periode etait analysee a clique
// dessus et les deux sont reparties.
const NOM_MI_TEMPS = { mt1: '1re période', mt2: '2e période' }

function phraseDeRelance(match) {
  const aRefaire = match?.analysis_relance_prevue
  if (!Array.isArray(aRefaire) || aRefaire.length === 0) return null

  const relancees = aRefaire.map((h) => NOM_MI_TEMPS[h]).filter(Boolean)
  if (!relancees.length) return null

  const gardees = ['mt1', 'mt2']
    .filter((h) => NOM_MI_TEMPS[h] && !aRefaire.includes(h) && match?.[`analysis_${h}_status`])
    .map((h) => NOM_MI_TEMPS[h])

  const debut = relancees.length === 1
    ? `Relance la ${relancees[0]}.`
    : 'Relance la 1re et la 2e période.'

  if (!gardees.length) return debut

  const suite = gardees.length === 1
    ? `La ${gardees[0]} est déjà analysée, elle est conservée.`
    : `Les ${gardees.join(' et la ')} sont déjà analysées, elles sont conservées.`
  return `${debut} ${suite}`
}

export default function MatchDetail() {
  const { matchId } = useParams()
  const navigate    = useNavigate()
  const { selectedSeason, currentSeason } = useSeason()
  const { user } = useAuth()
  const [match, setMatch]           = useState(null)
  const [isMobile, setIsMobile]     = useState(() => window.innerWidth < 768)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab]   = useState(
    () => SLUGS_ONGLETS[searchParams.get('tab')] || 'overview'
  )

  // Changer d'onglet met l'URL a jour, en REMPLACANT l'entree d'historique :
  // parcourir les onglets ne doit pas remplir le bouton Retour du navigateur.
  const changerOnglet = (id) => {
    setActiveTab(id)
    const slug = ONGLET_VERS_SLUG[id]
    const p = new URLSearchParams(searchParams)
    if (slug && slug !== 'apercu') p.set('tab', slug)
    else p.delete('tab')
    setSearchParams(p, { replace: true })
  }
  const [editingType, setEditingType] = useState(false)
  const [savingType, setSavingType]   = useState(false)
  const [toast, setToast]             = useState(null)
  const [showLineupModal, setShowLineupModal] = useState(false)
  const [showEventModal, setShowEventModal]   = useState(false)
  // Edition score inline · ORIENTEE CLUB, comme le bandeau qui la porte : le
  // champ de gauche est TOUJOURS le notre. Les noms le disent (`Us` / `Them`)
  // parce que `scoreHomeEdit` avait fini par etre ecrit tel quel dans
  // `score_home`, qui est le score de l'equipe qui RECOIT
  // (P0-SCORE-ECRITURE-001). La conversion se fait a l'enregistrement, par
  // `toRawScores`, et nulle part ailleurs.
  const [editingScore, setEditingScore] = useState(false)
  const [scoreUsEdit, setScoreUsEdit]       = useState('')
  const [scoreThemEdit, setScoreThemEdit]   = useState('')
  const [scoreUsHtEdit, setScoreUsHtEdit]   = useState('')
  const [scoreThemHtEdit, setScoreThemHtEdit] = useState('')
  const [savingScore, setSavingScore] = useState(false)
  const [editingFeeling, setEditingFeeling] = useState(false)
  const [feelingEdit, setFeelingEdit]       = useState('')
  const [commentEdit, setCommentEdit]       = useState('')
  const [savingFeeling, setSavingFeeling]   = useState(false)
  const [sendModalOpen, setSendModalOpen]   = useState(false)
  const [players, setPlayers]               = useState([])
  const [showDeleteMatchModal, setShowDeleteMatchModal] = useState(false)
  const [deleteMatchConfirm, setDeleteMatchConfirm]     = useState('')
  const [deleteMatchLoading, setDeleteMatchLoading]     = useState(false)
  const deleteMatchLockRef = useRef(false)
  const [showBuyAnalysesModal, setShowBuyAnalysesModal] = useState(false)
  const [showTimelineModal, setShowTimelineModal]       = useState(false)
  const [analyseLoading, setAnalyseLoading]             = useState(false)
  const [analysesDisponibles, setAnalysesDisponibles]   = useState(null)
  const [analysisStatus, setAnalysisStatus]             = useState(null)
  const [analyseResults, setAnalyseResults]             = useState({ mt1: null, mt2: null, full: null })
  const [analyseResultsLoading, setAnalyseResultsLoading] = useState(false)
  const [analyseResultsError, setAnalyseResultsError]   = useState(null)
  const [tlMarkers, setTlMarkers] = useState({ kickOff1: null, halfTime: null, kickOff2: null, fullTime: null })
  // P1-SPLIT-MATCHDETAIL-001 · l'etat d'orchestration vit en base, cet ecran le
  // lit. Ces deux states ne PORTENT pas l'etat du traitement — ils ne portent
  // que ce qu'on en affiche. La verite, c'est `match.video_processing_status`.
  // `splitPhase` note ce que CET ECRAN a deja constate de facon definitive :
  // null (rien de tranche, on suit) | 'termine' | 'failed'. Il ne remplace pas
  // la base, il empeche l'ecran de dependre d'un rafraichissement qui peut
  // echouer — sans lui, un refetch rate laissait le bandeau affiche pour
  // toujours alors que le suivi, lui, s'etait arrete.
  const [splitPhase, setSplitPhase]     = useState(null)   // null | 'termine' | 'failed'
  const [splitMessage, setSplitMessage] = useState('')
  // COULEURS-MAILLOT-001 · saisie de la modale timeline. Initialisées depuis le
  // match puis le club par l'effet plus bas, à l'ouverture de la modale.
  const { kitHome, kitAway } = useKitColorDefaults()
  // MODALE-TIMELINE-SCOPE-001 · la declaration telle que la modale la porte.
  // `null` = le coach n'a pas encore repondu · on ne suppose rien.
  const [tlScope, setTlScope] = useState(null)
  const [tlKitNous, setTlKitNous]       = useState('')
  const [tlKitAdverse, setTlKitAdverse] = useState('')
  const [videoSignedUrl, setVideoSignedUrl]             = useState(null)
  const [loadingVideoUrl, setLoadingVideoUrl]           = useState(false)
  const videoRefModal = useRef(null)
  const [currentTimeModal, setCurrentTimeModal] = useState(0)
  const [durationModal, setDurationModal]       = useState(0)
  // Ajout d'une video sur un match deja saisi (MATCH-VIDEO-ADD-001).
  const [envoiVideoEnCours, setEnvoiVideoEnCours] = useState(false)
  const [progressionVideo, setProgressionVideo] = useState(0)
  const [erreurVideo, setErreurVideo]           = useState('')

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    let cancelled = false
    fetch(`${API}/players/`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return
        setPlayers(Array.isArray(data) ? data : (data.players || []))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [matchId])

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000) }

  const handleDeleteMatch = async () => {
    if (deleteMatchLockRef.current || !match || deleteMatchConfirm !== match.opponent) return
    deleteMatchLockRef.current = true
    setDeleteMatchLoading(true)
    try {
      const res = await fetch(`${API}/matches/${matchId}`, { method: 'DELETE', headers: authHeaders() })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        showToast(body?.detail || 'Erreur lors de la suppression', 'error')
        return
      }
      showToast('Match supprimé', 'success')
      setTimeout(() => navigate('/dashboard/matches'), 1200)
    } catch {
      showToast('Erreur réseau', 'error')
    } finally {
      deleteMatchLockRef.current = false
      setDeleteMatchLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    Promise.all([
      fetch(`${API}/matches/${matchId}/`, { headers: authHeaders() }).then(r => {
        if (!r.ok) throw new Error('Match introuvable')
        return r.json()
      }),
      fetch(`${API}/matches/${matchId}/events/`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : [])
        .catch(() => []),
    ])
      .then(([matchData, eventsData]) => {
        if (cancelled) return
        setMatch({ ...matchData, events: Array.isArray(eventsData) ? eventsData : [] })
        setLoading(false)
      })
      .catch(e => {
        if (!cancelled) { setError(e.message); setLoading(false) }
      })
    return () => { cancelled = true }
  }, [matchId])

  // Poll match + analysis-status pendant PROCESSING
  useEffect(() => {
    if (!match) return
    const processing = match.status === 'PROCESSING' || match.status === 'processing'
    if (!processing) return

    let cancelled = false
    const tick = async () => {
      try {
        const [matchRes, statusRes] = await Promise.allSettled([
          fetch(`${API}/matches/${matchId}/`, { headers: authHeaders() }).then((r) => r.json()),
          matchService.getAnalysisStatus(matchId).catch(() => null),
        ])
        if (cancelled) return
        if (matchRes.status === 'fulfilled') setMatch(matchRes.value)
        if (statusRes.status === 'fulfilled' && statusRes.value) setAnalysisStatus(statusRes.value)
      } catch { /* tick suivant */ }
    }

    tick()
    const interval = setInterval(tick, 8000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [match?.status, matchId])

  // Relit le match en base. Preserve `events`, charges par un appel distinct :
  // les ecraser reviendrait a vider la liste des buts a chaque rafraichissement.
  const rafraichirMatch = useCallback(async () => {
    try {
      const r = await fetch(`${API}/matches/${matchId}/`, { headers: authHeaders() })
      if (!r.ok) return
      const data = await r.json()
      setMatch(prev => ({ ...data, events: prev?.events || [] }))
    } catch { /* le suivi rappellera, ou le prochain chargement tranchera */ }
  }, [matchId])

  // COULEURS-MAILLOT-001 · pré-remplissage à l'ouverture de la modale.
  // Le match d'abord (ce qui a déjà été saisi), le club ensuite (le jeu
  // correspondant au lieu). Rien n'est deviné au-delà : la couleur de
  // l'adversaire n'a aucun défaut possible et reste à saisir.
  useEffect(() => {
    if (!showTimelineModal || !match) return
    setTlKitNous(couleurNousParDefaut(match, { kit_color_home: kitHome, kit_color_away: kitAway }))
    setTlKitAdverse(match.kit_color_opponent || '')
    // On reprend la declaration si elle existe, et on laisse `null` sinon ·
    // un match qui a DEJA une video n'a aucun autre endroit ou declarer, le
    // bloc de choix de la page ne s'affichant que sur zero fichier. Mesure du
    // 11/08 · 14 matchs sont dans ce cas, dont celui du premier client payant.
    setTlScope(
      ['full_match', 'single_half', 'two_files'].includes(match.video_scope)
        ? match.video_scope
        : null
    )
  }, [showTimelineModal, match, kitHome, kitAway])

  // ── P1-SPLIT-MATCHDETAIL-001 · suivi de la decoupe · la base fait foi ──
  //
  // Ancre au montage, jamais a un clic. Les deux entrees de cette page
  // (« Analyser » du bandeau et la modale timeline) ne font plus qu'AMORCER la
  // decoupe : elles ecrivent en base et rendent la main. Tout ce qui suit est
  // ici, ce qui donne trois proprietes que la version precedente n'avait pas :
  //   · un tick rate ne rompt pas le suivi, il est simplement retente ;
  //   · chaque requete a un timeout, aucune ne peut suspendre la boucle ;
  //   · le retour de l'onglet au premier plan relance un tick immediatement,
  //     sans attendre que le navigateur redonne ses timers a un onglet endormi.
  //
  // Le rechargement de page est couvert sans rien ajouter : le `matchId` est
  // dans la route et `video_processing_status` vient du serveur. C'est ce qui
  // dispense cet ecran du `?split=1` du wizard, qui lui repartait a l'etape 1.
  //
  // Signal de fin : `has_halves`, soit video_url_mt1 ET video_url_mt2 en base —
  // la verite ecrite par le backend, et non un libelle de statut.
  const decoupeEnCours = DECOUPE_EN_COURS.has(match?.video_processing_status)

  useEffect(() => {
    // `splitPhase !== null` = cet ecran a deja vu la fin (ou l'echec). On ne
    // relance pas un suivi sur un traitement dont on connait l'issue, meme si
    // le dernier `match` charge porte encore un statut perime.
    if (!decoupeEnCours || splitPhase !== null || !matchId) return

    let annule  = false
    let timer   = null
    let termine = false
    const echeance = Date.now() + DECOUPE_ATTENTE_MAX_MS

    const programmer = () => {
      if (annule || termine) return
      timer = setTimeout(tick, DECOUPE_POLL_MS)
    }

    async function tick() {
      if (annule || termine) return

      if (Date.now() > echeance) {
        termine = true
        setSplitPhase('failed')
        setSplitMessage("La découpe prend plus longtemps que prévu. Elle continue côté serveur : recharge cette page pour en voir la suite.")
        return
      }

      let s
      try {
        s = await matchService.getVideoStatus(matchId, { timeout: DECOUPE_REQ_TIMEOUT_MS })
      } catch {
        // Reseau, 502, requete trop lente : sans consequence, on retente.
        programmer()
        return
      }
      if (annule || termine) return

      if (s.has_halves) {
        termine = true
        setSplitMessage('Mi-temps prêtes · lancement de l\'analyse…')
        // L'analyse est deja enchainee cote serveur depuis le 02/08/2026 et la
        // route est idempotente. On l'appelle quand meme pour couvrir les cas
        // ou l'autostart a renonce ; un echec ici ne bloque personne, le bouton
        // « Analyser » du bandeau reste la.
        try { await matchService.launchAnalysis(matchId) } catch { /* bouton du bandeau */ }
        if (annule) return
        // `termine` AVANT le rafraichissement, et pas apres : c'est
        // `has_halves` qui fait foi, pas le succes du refetch. Si le reseau
        // tombe juste ici, le bandeau s'efface quand meme — il annoncerait
        // sinon une decoupe en cours dont on sait qu'elle est finie.
        setSplitPhase('termine')
        setSplitMessage('')
        rafraichirMatch()
        return
      }

      if (s.status === 'failed') {
        termine = true
        setSplitPhase('failed')
        setSplitMessage(s.error || 'La découpe MT1 / MT2 a échoué.')
        rafraichirMatch()
        return
      }

      if (!DECOUPE_EN_COURS.has(s.status)) {
        // Retour a un etat de repos sans mi-temps : split reporte faute de
        // place disque, le plus souvent. Plus rien ne tourne, on cesse de
        // suivre et la page reaffiche de quoi relancer.
        termine = true
        setSplitPhase('termine')
        setSplitMessage('')
        rafraichirMatch()
        return
      }

      setSplitMessage(libelleDecoupe(s.status))
      programmer()
    }

    const auRetourDeLOnglet = () => {
      if (document.visibilityState !== 'visible') return
      clearTimeout(timer)
      tick()
    }

    tick()
    document.addEventListener('visibilitychange', auRetourDeLOnglet)
    return () => {
      annule = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', auRetourDeLOnglet)
    }
  }, [decoupeEnCours, splitPhase, matchId, rafraichirMatch])

  // Charge les résultats quand le match est analysé (ou analysis.aggregated_ready)
  useEffect(() => {
    if (!match) return
    const done = (
      match.status === 'ANALYZED' || match.status === 'analyzed' ||
      match.status === 'COMPLETED' || match.status === 'completed' ||
      match.analysis?.aggregated_ready ||
      analysisStatus?.aggregated_ready ||
      analysisStatus?.status === 'succeeded'
    )
    const hasLegacy = !!match.stats || !!match.ai_insights
    if (!done && !hasLegacy) return

    let cancelled = false
    setAnalyseResultsLoading(true)
    matchService.loadAnalysisResults(matchId).then((res) => {
      if (cancelled) return
      setAnalyseResultsError(res.error)
      // PAS DE REPLI. L'agregat du match complet vient du backend, ou n'existe
      // pas. Le client recalculait ici faute de `full`, en moyennant les
      // pourcentages — ce que le backend refuse depuis P0-STATS-001, faute de
      // durees de jeu effectif comparables. Il produisait en plus un agregat
      // prive de `indicateurs_derives`, donc different de celui du backend sur
      // les memes donnees, sans que rien ne le signale.
      setAnalyseResults({ mt1: res.mt1, mt2: res.mt2, full: res.full })
      // Enrichit stats legacy si absentes
      if (!match.stats && res.full) {
        const legacy = analyseResultToLegacyStats(res.full)
        if (legacy) setMatch((prev) => (prev?.stats ? prev : { ...prev, stats: legacy }))
      }
      setAnalyseResultsLoading(false)
    })
    return () => { cancelled = true }
  }, [
    matchId,
    match?.status,
    match?.analysis?.aggregated_ready,
    analysisStatus?.aggregated_ready,
    analysisStatus?.status,
  ])

  // L'ECRAN NE SE MET A JOUR QUE SI LA BASE A DIT OUI.
  //
  // `fetch` ne rejette que sur panne reseau. Un 400, un 403 « saison archivee »
  // ou un 500 traversaient donc le `try` sans encombre, et `setMatch` etait
  // appele juste apres : le coach voyait « Amical », la base gardait
  // « Championnat », et rien ne le lui disait.
  //
  // Le `catch` repondait a « le reseau a-t-il fonctionne ? » quand la seule
  // question qui compte est « l'enregistrement a-t-il eu lieu ? ».
  //
  // En cas d'echec on NE ferme PAS l'editeur · `value={match.type}` n'ayant pas
  // bouge, le select revient de lui-meme sur la valeur reellement en base.
  // C'est le meme patron que `handleDeleteMatch` plus haut.
  const handleUpdateType = async (newType) => {
    if (newType === match.type) { setEditingType(false); return }
    setSavingType(true)
    try {
      const res = await fetch(`${API}/matches/${matchId}/`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ type: newType }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        showToast(typeof body?.detail === 'string' ? body.detail : "Le type de match n'a pas pu être enregistré", 'error')
        return
      }
      setMatch(prev => ({ ...prev, type: newType }))
      showToast('Type de match mis à jour', 'success')
      setEditingType(false)
    } catch {
      showToast('Erreur réseau', 'error')
    } finally { setSavingType(false) }
  }

  // EXPORT PDF · le backend lance un Chromium qui ouvre CETTE page et l'imprime.
  //
  // LE BOUTON SE DESACTIVE PENDANT LA GENERATION, et il le montre. Compter 5 a
  // 8 s sur Render : sans etat visible, un coach clique trois fois et lance
  // trois Chromium sur un CPU unique, deja partage avec le pipeline video.
  //
  // Ne s'appuie PAS sur `match.pdf_url` — colonne jamais renseignee, code mort
  // documente dans backend#127. Le fichier est produit a la demande et n'est
  // stocke nulle part.
  const [pdfEnCours, setPdfEnCours] = useState(false)
  const handleDownloadPDF = async () => {
    if (pdfEnCours) return
    setPdfEnCours(true)
    try {
      const res = await fetch(`${API}/matches/${matchId}/report.pdf`, { headers: authHeaders() })
      if (!res.ok) throw new Error(String(res.status))
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `rapport-${match.opponent || 'match'}-${(match.date || '').slice(0, 10)}.pdf`
        .replace(/\s+/g, '-').toLowerCase()
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      showToast('Le rapport n\'a pas pu être généré', 'error')
    } finally {
      setPdfEnCours(false)
    }
  }

  const handleLineupSaved = (updated) => {
    setMatch(prev => ({ ...prev, lineup: updated.lineup || prev.lineup, formation: updated.formation || prev.formation }))
    setShowLineupModal(false)
    showToast('Composition enregistrée')
  }

  // Ouvre l'edition PRE-REMPLIE avec le score affiche. Les champs s'ouvraient
  // vides : le coach retapait tout, y compris la mi-temps qu'il ne voulait pas
  // toucher — et un champ MT laisse vide l'aurait effacee.
  const openScoreEdit = () => {
    const [us, them]     = scoresUsThem(match)
    const [usHt, themHt] = scoresUsThemHT(match)
    setScoreUsEdit(us   === null ? '' : String(us))
    setScoreThemEdit(them === null ? '' : String(them))
    setScoreUsHtEdit(usHt   === null ? '' : String(usHt))
    setScoreThemHtEdit(themHt === null ? '' : String(themHt))
    setEditingScore(true)
  }

  const handleSaveScore = async () => {
    const us   = parseInt(scoreUsEdit)
    const them = parseInt(scoreThemEdit)
    if (isNaN(us) || isNaN(them) || us < 0 || them < 0) return

    // La mi-temps est optionnelle, mais c'est LES DEUX ou AUCUN : « 2 – ? »
    // laisserait croire a un resultat de mi-temps (meme regle que
    // `scoresUsThemHT`, qui traite un seul chiffre comme une absence).
    const mtVide  = scoreUsHtEdit === '' && scoreThemHtEdit === ''
    const usHt    = parseInt(scoreUsHtEdit)
    const themHt  = parseInt(scoreThemHtEdit)
    if (!mtVide && (isNaN(usHt) || isNaN(themHt) || usHt < 0 || themHt < 0)) {
      showToast('Le score de mi-temps demande les deux chiffres', 'error')
      return
    }

    // CLUB -> TERRAIN. Le coach saisit « nous – eux » ; `score_home` est le
    // score de l'equipe qui RECOIT. Sans cette conversion, un match joue
    // dehors repartait a l'envers et se relisait a l'endroit
    // (P0-SCORE-ECRITURE-001).
    const [sh, sa]     = toRawScores(match, us, them)
    const [shHt, saHt] = mtVide ? [null, null] : toRawScores(match, usHt, themHt)
    const patch = {
      score_home: sh, score_away: sa,
      score_home_ht: shHt, score_away_ht: saHt,
    }

    setSavingScore(true)
    try {
      await fetch(`${API}/matches/${matchId}/`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify(patch),
      })
      setMatch(prev => ({ ...prev, ...patch }))
      setEditingScore(false)
      showToast('Score mis à jour')
    } catch { showToast('Erreur', 'error') }
    finally { setSavingScore(false) }
  }

  const handleSaveFeeling = async () => {
    if (!feelingEdit) return
    setSavingFeeling(true)
    try {
      await fetch(`${API}/matches/${matchId}/`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ coach_feeling: feelingEdit, coach_comment: commentEdit }),
      })
      setMatch(prev => ({ ...prev, coach_feeling: feelingEdit, coach_comment: commentEdit }))
      setEditingFeeling(false)
      showToast('Ressenti mis à jour')
    } catch { showToast('Erreur', 'error') }
    finally { setSavingFeeling(false) }
  }

  const handleEventAdded = async (ev) => {
    setMatch(prev => ({ ...prev, events: [...(prev.events || []), ev] }))
    setShowEventModal(false)
    showToast('Événement ajouté')
    try {
      const r = await fetch(`${API}/matches/${matchId}/events/`, { headers: authHeaders() })
      if (r.ok) {
        const fresh = await r.json()
        setMatch(prev => ({ ...prev, events: Array.isArray(fresh) ? fresh : prev.events }))
      }
    } catch {}
  }

  const handleDeleteEvent = async (evId) => {
    try {
      const res = await fetch(`${API}/matches/${matchId}/events/${evId}/`, {
        method: 'DELETE', headers: authHeaders(),
      })
      if (!res.ok) throw new Error()
      setMatch(prev => ({ ...prev, events: (prev.events || []).filter(e => e.id !== evId) }))
      showToast('Événement supprimé')
    } catch { showToast('Erreur', 'error') }
  }

  // ── Ajout d'une video sur un match deja saisi ───────────────────────
  //
  // POURQUOI CE CHEMIN EXISTE. Un coach (GFCA, 06/08/2026) a saisi sa feuille
  // de match complete — score, 21 joueurs, 2 evenements — puis son upload video
  // a echoue. Le match est reste en base avec video_url NULL, et AUCUN ecran ne
  // permettait d'ajouter la video apres coup : il aurait fallu tout ressaisir.
  //
  // La validation et l'envoi ne sont PAS reecrits ici · verifierFichierVideo et
  // uploadFileMultipart sont exactement ceux du wizard (MULTIPART-002). Un
  // second parseur de fichier ou une seconde file d'upload divergeraient au
  // premier correctif applique d'un seul cote.
  // PARCOURS-VIDEO-DECLARE-002 · fichiers choisis dans les emplacements
  // nommes. Creux · en `two_files` le coach peut n'avoir rempli que la 2e.
  const [fichiersPeriodes, setFichiersPeriodes] = useState([])

  /**
   * Envoie les emplacements remplis, l'un APRES l'autre.
   *
   * Deux routes distinctes, et il ne faut pas les confondre ·
   * · `single_half` -> PATCH `video_url` · c'est une source unique sans decoupe ;
   * · `two_files`   -> POST `/halves/` · ce sont des mi-temps FOURNIES, que
   *   `PATCH video_url` ne rattacherait pas et dont il effacerait les voisines.
   */
  const envoyerPeriodes = async () => {
    const aEnvoyer = fichiersPeriodes
      .map((f, i) => (f ? { fichier: f, index: i } : null))
      .filter(Boolean)
    if (!aEnvoyer.length || envoiVideoEnCours) return

    setErreurVideo('')
    setEnvoiVideoEnCours(true)
    setProgressionVideo(0)
    try {
      for (let rang = 0; rang < aEnvoyer.length; rang++) {
        const { fichier, index } = aEnvoyer[rang]
        const cle = await uploadFileMultipart(fichier, {
          onProgress: (p) => setProgressionVideo(
            Math.round((rang * 100 + p) / aEnvoyer.length)
          ),
        })
        const res = scopeDuMatch === 'two_files'
          ? await fetch(`${API}/matches/${matchId}/halves/`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ [index === 0 ? 'video_key_mt1' : 'video_key_mt2']: cle }),
            })
          : await fetch(`${API}/matches/${matchId}/`, {
              method: 'PATCH',
              headers: authHeaders(),
              body: JSON.stringify({ video_url: cle, video_scope: scopeDuMatch }),
            })
        if (!res.ok) {
          const corps = await res.json().catch(() => ({}))
          throw new Error(corps?.detail || "Vidéo envoyée mais impossible de la lier au match.")
        }
        // Rattachee des son arrivee · si le second envoi echoue, le premier est
        // deja en base et ne sera jamais redemande.
        const frais = await res.json().catch(() => null)
        if (frais) setMatch(prev => ({ ...prev, ...frais }))
      }
      setFichiersPeriodes([])
      showToast('Vidéo ajoutée · il reste à placer les repères')
    } catch (err) {
      setErreurVideo(err?.message || "Erreur pendant l'envoi de la vidéo.")
    } finally {
      setEnvoiVideoEnCours(false)
      setProgressionVideo(0)
    }
  }

  const handleEnvoyerVideo = async (fichier = null) => {
    const aEnvoyer = fichier
    if (!aEnvoyer || envoiVideoEnCours) return
    setErreurVideo('')
    setEnvoiVideoEnCours(true)
    setProgressionVideo(0)
    try {
      const fileKey = await uploadFileMultipart(aEnvoyer, { onProgress: setProgressionVideo })

      // Slash final, comme partout ailleurs dans ce fichier. La route backend
      // est declaree sans slash mais la redirection est same-origin : l'en-tete
      // Authorization survit, ce que prouve l'edition de score qui passe par la
      // meme forme depuis toujours.
      // PARCOURS-VIDEO-DECLARE-001 · une SECONDE PERIODE ne passe pas par
      // `PATCH video_url`. Cette route remplace la SOURCE du match · elle
      // effacerait la premiere mi-temps si celle-ci avait ete decoupee, et
      // surtout elle ne rattacherait rien a `video_url_mt2`. La route dediee
      // marque l'origine `provided` et n'efface rien.
      const secondePeriode = (
        match?.video_scope === 'two_files' &&
        Boolean(match?.video_url_mt1) && !match?.video_url_mt2
      )
      const res = secondePeriode
        ? await fetch(`${API}/matches/${matchId}/halves/`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ video_key_mt2: fileKey }),
          })
        : await fetch(`${API}/matches/${matchId}/`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ video_url: fileKey }),
          })
      if (!res.ok) {
        const corps = await res.json().catch(() => ({}))
        const detail = typeof corps?.detail === 'string' ? corps.detail : corps?.detail?.message
        throw new Error(detail || "Vidéo envoyée mais impossible de la lier au match.")
      }

      // La reponse du PATCH est le match serialise : on la prend telle quelle
      // plutot que de deviner l'etat resultant. Le backend a aussi remis
      // video_processing_status a PENDING, et lui seul sait ce qu'il a ecrit.
      const frais = await res.json().catch(() => null)
      setMatch(prev => (frais ? { ...prev, ...frais } : { ...prev, video_url: fileKey, has_video: true }))
      // NE PAS ANNONCER UNE FIN QUI N'EN EST PAS UNE.
      //
      // « Vidéo ajoutée au match » laissait croire que c'était terminé · le
      // backend vient de poser `video_processing_status = pending`, et sans
      // repères rien ne démarrera jamais. C'est ce chemin, construit pour un
      // coach dont l'upload a déjà échoué une fois, qui fabriquait le plus
      // sûrement un cul-de-sac. Le bandeau ci-dessous prend le relais, ce
      // message dit seulement la vérité en attendant.
      showToast('Vidéo ajoutée · il reste à placer les repères')
    } catch (err) {
      setErreurVideo(err?.message || "Erreur pendant l'envoi de la vidéo.")
      // CE CHEMIN ETAIT LE SEUL DES TROIS A N'EMETTRE RIEN.
      //
      // CreateMatch et uploadService instrumentent leur echec depuis
      // UPLOAD-ERR-001 ; ce bloc, ajoute apres eux, avait repris leur
      // validation et leur upload mais pas leur instrumentation. Or c'est
      // precisement le chemin construit pour un coach dont l'upload a deja
      // echoue une fois · un echec y etait invisible.
      //
      // Meme filtre metier que les deux autres · un quota FREE atteint ou un
      // format refuse est une reponse du produit, pas une panne, et les
      // remonter noierait les vrais refus S3.
      //
      // JAMAIS le nom du fichier : un nom de fichier coach porte
      // regulierement le nom du club, de l'adversaire ou d'un joueur.
      // L'extension seule repond a la question posee.
      if (!err.metier) Sentry.captureException(err, {
        tags: { chantier: 'upload_s3_failed', s3_code: err.s3Code || 'inconnu' },
        extra: {
          http_status:     err.httpStatus,
          s3_code:         err.s3Code,
          s3_message:      err.s3Message,
          s3_request_id:   err.s3RequestId,
          // `aEnvoyer`, PAS `fichierVideo` · ce dernier n'est plus jamais
          // renseigne depuis que le bloc generique a disparu
          // (PARCOURS-VIDEO-DECLARE-004). Le laisser aurait vide ces trois
          // extras en silence · une capture Sentry sans charge utile est pire
          // qu'une absence de capture, elle fait croire qu'on a regarde.
          file_size_bytes: aEnvoyer?.size,
          file_type:       aEnvoyer?.type,
          file_extension:  aEnvoyer?.name?.split('.').pop(),
        },
      })
    } finally {
      setEnvoiVideoEnCours(false)
    }
  }

  const fetchQuota = useCallback(async () => {
    try {
      const r = await fetch(`${API}/matches/credits/status`, { headers: authHeaders() })
      if (r.ok) {
        const d = await r.json()
        setAnalysesDisponibles(d.analyses_disponibles ?? 0)
        return d.analyses_disponibles ?? 0
      }
    } catch {}
    return 0
  }, [])

  // BANDEAU-SANS-QUOTA-001 · le solde n'est charge QUE si le bandeau s'affiche
  // ET que le compte est payant · un compte FREE est deja tranche par
  // `estVraiFree`, sans aucun aller-retour. Une seule fois par page.
  //
  // PLACE APRES `fetchQuota`, et ce n'est pas un detail · `fetchQuota` est un
  // `const`, donc dans sa zone morte temporelle au-dessus. Le tableau de
  // dependances est evalue au RENDU, pas a l'execution du callback · l'y
  // referencer plus haut leve un ReferenceError des le premier rendu.
  //
  // La condition est volontairement plus large que `videoAPreparer` (pas de
  // `isArchived`, qui n'est calcule qu'apres les early returns) · un appel de
  // trop sur un match archive ne coute rien, une exception en couterait une.
  useEffect(() => {
    if (analysesDisponibles !== null) return
    if (estVraiFree(user)) return
    const enAttenteDeReperes = (
      Boolean(match?.video_url) && !match?.video_url_mt1 && !match?.video_url_mt2 &&
      match?.video_processing_status === 'pending'
    )
    if (!enAttenteDeReperes) return
    fetchQuota()
  }, [match, analysesDisponibles, user, fetchQuota])

  const fetchVideoUrl = useCallback(async () => {
    setLoadingVideoUrl(true)
    try {
      const r = await fetch(`${API}/matches/${matchId}/video-url`, { headers: authHeaders() })
      if (r.ok) {
        const d = await r.json()
        setVideoSignedUrl(d.video_url || null)
      }
    } catch {}
    finally { setLoadingVideoUrl(false) }
  }, [matchId])

  const handleCallAnalyze = useCallback(async () => {
    // UNE DECOUPE EN COURS NE SE RELANCE PAS.
    //
    // Cette garde remplace une protection qui existait par accident : avant,
    // `analyseLoading` restait vrai pendant toute la decoupe — la sequence
    // entiere etant attendue dans ce clic — et desactivait le bouton. Le clic
    // rendant desormais la main tout de suite, le bouton redevient cliquable
    // pendant que ca decoupe, et un second clic lancerait un second
    // `process_half_split` sur le meme match.
    //
    // C'est une garde de handler, pas un etat visuel : l'apparence du bouton
    // pendant le traitement appartient a P1-UX-SPLIT-001.
    if (decoupeEnCours) { showToast('Découpe déjà en cours…'); return }
    setAnalyseLoading(true)
    try {
      // Si source presente + marqueurs mais pas encore de mi-temps → split d'abord
      const needsSplit = match?.video_url && !(match?.video_url_mt1 && match?.video_url_mt2)
      const hasMarkers = (
        match?.timeline_kick_off_1 != null &&
        match?.timeline_half_time != null &&
        match?.timeline_kick_off_2 != null &&
        match?.timeline_full_time != null
      )
      // P1-SPLIT-MATCHDETAIL-001 · on AMORCE la decoupe et on rend la main.
      // Le suivi monte ci-dessus prend le relais et lancera l'analyse a la fin.
      // Avant, `pollUntilHalvesReady` etait attendu ici meme : la sequence
      // entiere tenait dans la closure de ce clic, un seul maillon interrompu
      // et l'ecran ne revenait jamais.
      if (needsSplit && hasMarkers) {
        await matchService.splitHalves(matchId)
        // Ce que le serveur vient d'ecrire en base, avant meme de repondre
        // (routes/matches.py:667). On le recopie pour que le bandeau paraisse
        // sans attendre un aller-retour ; un rechargement lira la meme valeur.
        setMatch(prev => ({ ...prev, video_processing_status: 'splitting' }))
        setSplitPhase(null)
        setSplitMessage(libelleDecoupe('splitting'))
        showToast('Découpe MT1 / MT2 lancée')
        return
      }
      const r = await fetch(`${API}/matches/${matchId}/analyze`, {
        method: 'POST', headers: authHeaders(),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        const detail = typeof body?.detail === 'string' ? body.detail : body?.detail?.message
        showToast(detail || 'Erreur lors du lancement', 'error')
        return
      }
      setMatch(prev => ({ ...prev, status: 'PROCESSING', video_url: null, has_video: true }))
      showToast('Analyse lancée !')
    } catch (err) {
      showToast(err?.message || 'Erreur réseau', 'error')
    } finally {
      setAnalyseLoading(false)
    }
  }, [matchId, match, decoupeEnCours])

  const handleLancerAnalyse = useCallback(async () => {
    // UN PLAN « FREE » NE DIT PAS QUI PAIE.
    //
    // Un coach membre de club porte un plan PERSONNEL FREE tout en etant
    // couvert par l'abonnement de son club · il consomme le quota du directeur
    // sportif, pas un quota personnel. Le detecteur vit dans planAccess.js,
    // une seule definition partagee avec hasStudioAccess · deux lectures du
    // meme fait divergeraient au premier correctif applique d'un seul cote.
    //
    // Sans cette distinction, la redirection ci-dessous envoyait vers la page
    // d'abonnement des CLIENTS PAYANTS venus lancer une analyse. Cas reel ·
    // GFCA / Gazelec Ajaccio, 06/08/2026 · plan FREE, club_id renseigne, quota
    // porte par ds-gazelec.
    const isVraiFree = !user?.is_superadmin && estVraiFree(user)

    // Le vrai compte d'essai, lui, garde son chemin de conversion intact.
    if (isVraiFree) {
      navigate('/dashboard/settings')
      return
    }

    let dispo = analysesDisponibles
    if (dispo === null) {
      dispo = await fetchQuota()
    }

    if (dispo <= 0) {
      setShowBuyAnalysesModal(true)
      return
    }

    // Les reperes REQUIS dependent de ce qui a ete filme · exiger « fin MT1 »
    // et « coup d'envoi MT2 » sur une periode unique, c'est renvoyer
    // indefiniment vers une modale que le coach ne peut pas satisfaire.
    const reperes = { kickOff1:'timeline_kick_off_1', halfTime:'timeline_half_time',
                      kickOff2:'timeline_kick_off_2', fullTime:'timeline_full_time' }
    const hasMarkers = marqueursPour(match.video_scope)
      .every(m => match[reperes[m.key]] != null)

    // LES COULEURS SONT UNE CONDITION AU MEME TITRE.
    //
    // En deux fichiers, `hasMarkers` est vrai SANS RIEN AVOIR DEMANDE (la liste
    // est vide) · sans cette seconde condition, un match a deux fichiers
    // partirait a l'analyse sans maillots, alors que `CreateMatch` les exige
    // depuis le 11/08 et que le prestataire s'en sert pour nommer les equipes.
    // C'est aussi ce qui donne un objet a la modale quand il n'y a aucun repere.
    const couleursFaites = couleursCompletes(match.kit_color_us, match.kit_color_opponent)

    if (!hasMarkers || !couleursFaites) {
      setTlMarkers({
        kickOff1: match.timeline_kick_off_1 ?? null,
        halfTime: match.timeline_half_time  ?? null,
        kickOff2: match.timeline_kick_off_2 ?? null,
        fullTime: match.timeline_full_time  ?? null,
      })
      // Aucun repere a pointer · aucune video a charger. En deux fichiers
      // `video_url` est vide de toute facon, et la modale affichait donc un
      // « Vidéo non disponible » qui ressemblait a une panne.
      if (marqueursPour(match.video_scope).length > 0) await fetchVideoUrl()
      setShowTimelineModal(true)
      return
    }

    await handleCallAnalyze()
  }, [user, analysesDisponibles, match, fetchQuota, fetchVideoUrl, handleCallAnalyze, navigate])

  // P1-SPLIT-MATCHDETAIL-001 · meme traitement que `handleCallAnalyze` : on
  // enregistre les marqueurs, on amorce la decoupe, on rend la main au suivi
  // monte. `validateTimelineAndAnalyze` faisait ici PATCH → split → poll →
  // analyze d'un seul tenant, dans la closure de ce clic.
  const handleValidateTimeline = useCallback(async () => {
    // Deux maillots identiques ne distinguent rien, et vaudraient un 422 du
    // prestataire une fois la decoupe deja payee.
    if (tlKitNous && tlKitNous === tlKitAdverse) {
      showToast("Les deux équipes ne peuvent pas porter la même couleur de maillot.", 'error')
      return
    }
    // Deux fichiers · la liste est vide, il n'y a rien a poser ni a effacer
    // cote coach, mais tout a remettre a null cote base (voir plus bas).
    const sansMarqueurs = marqueursPour(tlScope).length === 0

    setAnalyseLoading(true)
    try {
      await matchService.updateMatch(matchId, {
        // Aucun repere demande (deux fichiers) · les QUATRE partent a null.
        // Pas « on n'y touche pas » · un coach qui a repondu « match entier »,
        // pose ses quatre reperes, puis s'est corrige en « deux fichiers »
        // laisserait quatre valeurs perimees, soit exactement ce que
        // `recovery.py:142` prend pour une decoupe a relancer.
        timeline_kick_off_1: sansMarqueurs ? null : tlMarkers.kickOff1,
        // Sur une periode unique ces deux reperes N'EXISTENT PAS · on envoie
        // explicitement `null` plutot que de laisser trainer la valeur d'un
        // choix precedent. Quatre reperes non nuls sont ce que
        // `recovery.py:142` exige pour relancer une decoupe · en laisser deux a
        // NULL est ce qui empeche une reprise de decouper une seule mi-temps.
        timeline_half_time:  sansMarqueurs || tlScope === 'single_half' ? null : tlMarkers.halfTime,
        timeline_kick_off_2: sansMarqueurs || tlScope === 'single_half' ? null : tlMarkers.kickOff2,
        timeline_full_time:  sansMarqueurs ? null : tlMarkers.fullTime,
        // Les couleurs partent DANS LE MEME PATCH que les marqueurs, avant le
        // split · l'analyse s'enchaine toute seule derriere, il n'y a pas de
        // second passage pour les poser.
        kit_color_us:       tlKitNous || null,
        kit_color_opponent: tlKitAdverse || null,
        // La declaration part AVEC les marqueurs · c'est elle qui dit au
        // backend si ce match attend une decoupe. `video_scope` est applique
        // avant `video_url` cote route, donc l'ordre du corps est sans effet.
        video_scope:        tlScope,
      })
      // PAS DE DECOUPE HORS MATCH ENTIER · le backend la REFUSE en 400 depuis
      // `PARCOURS-VIDEO-DECLARE-001`, et il a raison · `half_split.py` couperait
      // au hasard une video qui n'a qu'une periode. Le match est deja `ready`
      // (le PATCH du scope l'a pose), on lance donc l'analyse directement.
      //
      // C'est aussi ce qui remplace l'enchainement automatique · sans decoupe,
      // `maybe_autostart_after_split` ne se declenche jamais.
      if (tlScope !== 'full_match') {
        try { await matchService.launchAnalysis(matchId) } catch { /* bouton du bandeau */ }
        setShowTimelineModal(false)
        setSplitPhase(null)
        setSplitMessage('')
        rafraichirMatch()
        showToast('Analyse lancée')
        return
      }

      await matchService.splitHalves(matchId)
      // `status` n'est PLUS force a PROCESSING ici : l'analyse n'est pas encore
      // lancee a cet instant, elle le sera par le suivi quand les deux mi-temps
      // seront ecrites. L'annoncer maintenant afficherait un match « en cours
      // d'analyse » pendant toute la decoupe, et masquerait le bandeau.
      setMatch(prev => ({
        ...prev,
        timeline_kick_off_1: tlMarkers.kickOff1,
        timeline_half_time:  tlMarkers.halfTime,
        timeline_kick_off_2: tlMarkers.kickOff2,
        timeline_full_time:  tlMarkers.fullTime,
        kit_color_us:        tlKitNous || null,
        kit_color_opponent:  tlKitAdverse || null,
        video_processing_status: 'splitting',
      }))
      setShowTimelineModal(false)
      setSplitPhase(null)
      setSplitMessage(libelleDecoupe('splitting'))
      showToast('Découpe MT1 / MT2 lancée')
    } catch (err) {
      showToast(err?.message || err?.response?.data?.detail || 'Erreur réseau', 'error')
    } finally {
      setAnalyseLoading(false)
    }
  }, [matchId, tlMarkers, tlKitNous, tlKitAdverse, tlScope, rafraichirMatch])

  function poseMarkerModal(key) {
    setTlMarkers(prev => ({ ...prev, [key]: Math.round(currentTimeModal) }))
  }

  function adjustMarkerModal(key, delta) {
    setTlMarkers(prev => {
      const cur = prev[key] ?? Math.round(currentTimeModal)
      return { ...prev, [key]: Math.max(0, cur + delta) }
    })
  }

  if (loading) return (
    <DashboardLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <div style={{ width: 24, height: 24, border: `2px solid ${GOLD_BDR}`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin .7s linear infinite', marginRight: 12 }} />
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED }}>Chargement...</span>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </DashboardLayout>
  )

  if (error || !match) return (
    <DashboardLayout>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 16 }}>
        <AlertCircle size={32} color={RED} />
        <div style={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{error || 'Match introuvable'}</div>
        <button onClick={() => navigate(-1)} style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: GOLD, background: 'none', border: `1px solid ${GOLD_BDR}`, padding: '10px 20px', cursor: 'pointer' }}>← Retour</button>
      </div>
    </DashboardLayout>
  )

  const isHome = isHomeMatch(match)
  const aUneVideo = Boolean(match.has_video || match.video_url || (match.video_url_mt1 && match.video_url_mt2))
  // LE NOM DU CLUB VIENT DE NOTRE BASE, jamais du payload du prestataire.
  // Ici le score est saisi par le coach : aucun risque d'attribution, contrairement
  // au rapport d'analyse qui dit « Nous » (P0-BUTS-ATTRIBUTION-001).
  // Deux utilisateurs sur 62 n'ont pas de club rattache — d'ou le repli.
  const nomClub = (user?.club_name || '').trim() || 'Mon équipe'
  // TOUT ce bandeau est du point de vue du CLUB — le nom du club a gauche, son
  // score a gauche, l'edition dans le meme ordre. Il ne reproduit PAS l'ordre
  // du terrain : un commentaire l'affirmait ici, au-dessus de deux variables
  // brutes que plus personne ne lisait, et c'est ce qui a fait relire
  // l'edition inline comme correcte pendant onze jours
  // (P0-SCORE-ECRITURE-001).
  const [scoreUs, scoreThem]     = scoresUsThem(match)
  const [scoreUsHt, scoreThemHt] = scoresUsThemHT(match)
  const hasScore = scoreUs !== null
  const won  = isWin(match)
  const lost = isDefeat(match)
  const resultColor = !hasScore ? GOLD : won ? GREEN : lost ? RED : GOLD
  const resultLabel = !hasScore ? '—' : won ? 'Victoire' : lost ? 'Défaite' : 'Nul'

  const isArchived = !!match.season && match.season < currentSeason

  // MODALE-TIMELINE-SCOPE-001 · CE QUE LE BOUTON EXIGE · trois conditions.
  //
  // La declaration d'abord · sans elle on ne sait pas COMBIEN de reperes
  // demander, et en supposer quatre est exactement le mur qu'on corrige.
  // Puis les reperes REELLEMENT demandes, deux ou quatre selon la reponse.
  // Puis les couleurs · apres ce bouton, il n'y a plus de moment pour les
  // poser (le serveur enchaine l'analyse tout seul).
  //
  // `tlScope` EST UN GARDE-FOU QU'AUCUN TEMOIN NE DISTINGUE. Mutation M2 · le
  // remplacer par `true` ne fait tomber aucune assertion, et c'est logique ·
  // sans declaration aucun repere n'est rendu, donc `every` est deja faux. Il
  // est garde a dessein · le jour ou les reperes s'afficheront avant la
  // reponse, il empechera de partir en analyse avec `video_scope: null`, que
  // le backend lirait comme « match entier ». C'est une redondance assumee,
  // pas une couverture.
  const modaleSansMarqueurs = marqueursPour(tlScope).length === 0

  const modalePrete = Boolean(
    tlScope &&
    marqueursPour(tlScope).every(m => tlMarkers[m.key] != null) &&
    couleursCompletes(tlKitNous, tlKitAdverse)
  )

  // Le bloc d'ajout de video n'apparait que sur un match REELLEMENT depourvu de
  // video. Trois exclusions, chacune pour une raison distincte :
  //
  // - mt1 OU mt2 non nul · le PATCH video_url EFFACE les deux mi-temps cote
  //   backend. Un coach qui a deja une decoupe ne doit pas pouvoir la perdre
  //   depuis cet ecran, sans le savoir. Le ET de `aUneVideo` ne suffit donc pas
  //   ici, il faut un OU.
  // - has_video · derive des memes colonnes cote serveur, mais cette page le
  //   pose localement a true avec video_url a null juste apres un lancement
  //   d'analyse (handleCallAnalyze). Sans ce garde, le bloc surgirait a ce
  //   moment precis pour proposer un reupload.
  // - isArchived · le backend refuse le PATCH en 403 sur une saison archivee.
  //   Proposer un envoi de plusieurs gigaoctets qui finira en 403 apres
  //   quarante minutes est pire que ne rien proposer.
  const peutAjouterVideo = (
    !isArchived &&
    !match.has_video &&
    !match.video_url &&
    !match.video_url_mt1 &&
    !match.video_url_mt2
  )

  // PARCOURS-VIDEO-DECLARE-001 · première période reçue, seconde attendue.
  //
  // ON NE SUPPRIME RIEN, JAMAIS. Un coach qui a envoyé 4 Go et fermé son onglet
  // n'a pas forcément abandonné — batterie, réseau, remis à demain. Lui faire
  // recommencer un upload de plusieurs Go est précisément ce qui l'a déjà fait
  // partir. Cohérent avec DATA-001 · les données sportives ne sont jamais
  // supprimées physiquement.
  //
  // Deux actions, aucune perte · ajouter la seconde, ou analyser celle-ci
  // seule. La seconde est légitime : une période analysée vaut mieux qu'un
  // match qui attend un fichier qui ne viendra peut-être jamais.
  // PARCOURS-VIDEO-DECLARE-002 · LA PAGE DU MATCH LIT LA DECLARATION.
  //
  // Elle l'ignorait completement · un coach qui declarait « deux fichiers »
  // puis quittait le wizard retombait sur « Ajouter la vidéo du match · Choisir
  // un fichier », un champ generique unique. Releve en production sur 172ff259.
  //
  // NULL se lit `full_match` · c'est le comportement d'avant la declaration, et
  // celui des 70 matchs d'historique.
  // PARCOURS-VIDEO-DECLARE-004 · le coach A-T-IL REPONDU ?
  //
  // A ne pas confondre avec `scopeDuMatch` juste en dessous, qui resout NULL en
  // `full_match` pour DECIDER D'UN TRAITEMENT. Ici la question est autre · faut-il
  // encore la poser ? Un NULL resolu afficherait une zone d'upload sur une
  // question sans reponse.
  const declarationFaite = ['full_match', 'single_half', 'two_files'].includes(match.video_scope)

  const scopeDuMatch = (
    ['full_match', 'single_half', 'two_files'].includes(match.video_scope)
      ? match.video_scope
      : 'full_match'
  )

  const attendSecondePeriode = (
    !isArchived &&
    scopeDuMatch === 'two_files' &&
    Boolean(match.video_url_mt1) &&
    !match.video_url_mt2 &&
    match.status !== 'ANALYZED' && match.status !== 'analyzed' &&
    match.status !== 'PROCESSING' && match.status !== 'processing'
  )

  // PARCOURS-MITEMPS-UNIQUE-001 · une vidéo est là, aucune mi-temps n'existe,
  // rien ne tourne, et le match n'est pas analysé · il manque les repères.
  //
  // `video_processing_status === 'pending'` et pas « différent de ready » · un
  // `failed` porte son propre message et un `splitting` a déjà son bandeau.
  // On ne parle que de l'état où le coach est le seul à pouvoir débloquer.
  // ── BANDEAU-SANS-QUOTA-001 · DIRE LA VERITE AVANT LE CLIC ──────────
  //
  // Le bandeau promettait « Placer les repères » et deposait le coach sur ses
  // reglages, sans un mot · `handleLancerAnalyse` court-circuite avant tout
  // appel API pour un compte sans droit d'analyse. Promettre une action et en
  // livrer une autre est PIRE que le silence qu'on avait corrige la veille ·
  // on avait rendu visible un chemin qui ne mene pas ou il dit.
  //
  // Cas reel · laborde.lucas@free.fr, deux matchs entiers en attente, donc
  // DEUX bandeaux promettant la meme chose et deposant au meme endroit.
  //
  // `estVraiFree` seul ne suffit pas · il couvre le plan FREE (quota 0 par
  // construction) mais pas un plan payant a quota epuise. Le second demande le
  // solde, charge par l'effet plus haut — et UNIQUEMENT quand le bandeau
  // s'affiche, soit 9 matchs sur 70 en production. Tant que le solde est
  // inconnu sur un compte payant, on reste sur le libelle nominal · un
  // abonne peut analyser dans l'immense majorite des cas, et la modale d'achat
  // couvre le reste.
  const analyseBloquee = (
    estVraiFree(user) ||
    (analysesDisponibles !== null && analysesDisponibles <= 0)
  )

  // LE DROIT A L'ESSAI SE LIT SUR LE COMPTE, SANS APPEL.
  //
  // Meme regle que le backend au moment du paiement · `already_trialed =
  // trial_ends_at is not None` (routes/subscription.py). On n'utilise PAS la
  // regle plus large de `SubscriptionManagement` · elle refuserait l'essai a
  // des comptes a qui le backend l'accorde, et promettre MOINS que ce qui sera
  // fait vaut mieux que l'inverse — mais promettre juste vaut mieux encore.
  const aDroitALEssai = !user?.trial_ends_at

  const videoAPreparer = (
    !isArchived &&
    Boolean(match.video_url) &&
    !match.video_url_mt1 &&
    !match.video_url_mt2 &&
    match.video_processing_status === 'pending' &&
    match.status !== 'ANALYZED' && match.status !== 'analyzed' &&
    match.status !== 'PROCESSING' && match.status !== 'processing'
  )

  const hasLineup = match.lineup && (Array.isArray(match.lineup) ? match.lineup.length > 0 : (match.lineup.starters || []).length > 0)
  const lineupArray = hasLineup ? (Array.isArray(match.lineup) ? match.lineup : [...(match.lineup.starters || []), ...(match.lineup.subs || [])]) : []
  const playersById    = new Map(players.map(p => [p.id, p]))
  const resolvedLineup = resolveLineupPlayers(lineupArray, playersById)

  const hasIaReport = !!(analyseResults.mt1 || analyseResults.mt2 || analyseResults.full)

  const texteRelance = phraseDeRelance(match)

  // PAS DE useMemo ICI · ce point du composant est situe APRES plusieurs
  // early returns. Un hook y serait appele conditionnellement — exactement le
  // defaut de CreateMatch documente dans P1-HOOKS-CREATEMATCH-001, que le lint
  // vient d'attraper. Le calcul parcourt trois objets, il ne merite pas de
  // memoisation.
  const versionFormule = versionFormuleLaPlusBasse(analyseResults)
  const tabs = [
    { id: 'overview', label: "Vue d'ensemble", show: true },
    { id: 'players',  label: 'Joueurs',         show: hasLineup || !!match.player_stats },
    { id: 'ia',       label: 'Rapport',      show: hasIaReport || analyseResultsLoading },
    { id: 'insights', label: 'Insights',        show: !!match.ai_insights },
    { id: 'report',   label: 'Rapport PDF',      show: !!match.pdf_url },
  ].filter(t => t.show)

  // REPLI SI L'ONGLET DEMANDE N'EXISTE PAS. Un lien `?tab=rapport` peut arriver
  // sur un match dont l'analyse n'est pas prete : sans ce repli, l'onglet actif
  // ne correspond a rien et la page s'affiche vide, sans un mot. Le harnais l'a
  // attrape — la branche `MatchDetailRapport` rendait 302 caracteres au lieu
  // de 451, un ecran presque nu.
  const ongletActif = tabs.some(t => t.id === activeTab) ? activeTab : 'overview'

  const typeLabel = (t) => ({
    CHAMPIONNAT: 'Championnat', COUPE: 'Coupe', AMICAL: 'Amical',
    championnat: 'Championnat', coupe: 'Coupe', amical: 'Amical',
  }[t] || t)

  return (
    <DashboardLayout>
      <style>{globalStyles}{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes toastIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, padding: '12px 24px', background: toast.type === 'error' ? RED : GOLD, color: toast.type === 'error' ? '#fff' : D, fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700, animation: 'toastIn .25s ease-out' }}>
          {toast.msg}
        </div>
      )}

      {/* Modal composition */}
      {showLineupModal && (
        <LineupModal
          matchId={matchId}
          category={match.category}
          formation={match.formation}
          onSave={handleLineupSaved}
          onClose={() => setShowLineupModal(false)}
        />
      )}

      {/* Modal événement */}
      {showEventModal && (
        <EventModal
          matchId={matchId}
          category={match.category}
          onSave={handleEventAdded}
          onClose={() => setShowEventModal(false)}
        />
      )}

      {/* Modal envoi cartes */}
      {sendModalOpen && (
        <SendCardsModal
          isOpen={sendModalOpen}
          onClose={() => setSendModalOpen(false)}
          matchId={matchId}
          matchInfo={{
            // Orientes club : ces valeurs partent aux familles par WhatsApp.
            scoreUs: scoreUs,
            scoreThem: scoreThem,
            opponentName: match.opponent,
            date: match.date,
          }}
          lineup={resolvedLineup}
          onSuccess={() => {}}
          onPlayerUpdated={(playerId, updates) => {
            setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, ...updates } : p))
            showToast(updates?.phone ? 'Téléphone enregistré' : 'Email enregistré')
          }}
        />
      )}

      {/* BuyAnalysesModal */}
      {showBuyAnalysesModal && (
        <BuyAnalysesModal onClose={() => setShowBuyAnalysesModal(false)} />
      )}

      {/* Timeline modal */}
      {showTimelineModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: D, border: `1px solid ${GOLD_BDR}`, width: '100%', maxWidth: 640, maxHeight: '92vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Header modal */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid rgba(79,142,247,0.15)` }}>
              <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD }}>
                {modaleSansMarqueurs ? 'Lancer l\'analyse' : 'Marqueurs vidéo'}
              </span>
              <button onClick={() => setShowTimelineModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(245,242,235,0.5)', padding: 4 }}><X size={16} /></button>
            </div>

            <div style={{ padding: '20px', flex: 1 }}>
              {/* Video player · AUCUN quand aucun repere n'est demande.
                  En deux fichiers `video_url` est vide · le bloc de repli
                  affichait « Vidéo non disponible », qui se lit comme une
                  panne alors que les deux fichiers sont bien la. */}
              {modaleSansMarqueurs ? (
                <p style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(245,242,235,0.55)', lineHeight: 1.65, margin: '0 0 20px' }}>
                  Tes deux fichiers sont enregistrés, une période chacun. Il n'y a
                  aucun repère à poser : chaque fichier est déjà une période complète.
                </p>
              ) : loadingVideoUrl ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, gap: 10, color: GOLD, fontFamily: MONO, fontSize: 10 }}>
                  <div style={{ width: 18, height: 18, border: `2px solid ${GOLD_BDR}`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
                  Chargement vidéo...
                </div>
              ) : videoSignedUrl ? (
                <div style={{ position: 'relative', marginBottom: 20 }}>
                  <video
                    ref={videoRefModal}
                    src={videoSignedUrl}
                    style={{ width: '100%', maxHeight: 260, background: '#000', display: 'block' }}
                    controls
                    onTimeUpdate={() => setCurrentTimeModal(videoRefModal.current?.currentTime ?? 0)}
                    onLoadedMetadata={() => setDurationModal(videoRefModal.current?.duration ?? 0)}
                  />
                  {/* Timeline scrubber overlay */}
                  {durationModal > 0 && (
                    <div style={{ position: 'relative', height: 4, background: 'rgba(245,242,235,0.1)', marginTop: 6, borderRadius: 2 }}>
                      {marqueursPour(tlScope).map(m => tlMarkers[m.key] != null && (
                        <div key={m.key} style={{ position: 'absolute', top: -4, width: 2, height: 12, background: m.color, borderRadius: 1, left: `${(tlMarkers[m.key] / durationModal) * 100}%`, transform: 'translateX(-50%)' }} title={`${m.label}: ${fmtTime(tlMarkers[m.key])}`} />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(245,242,235,0.4)', textAlign: 'center', padding: '24px 0', marginBottom: 20 }}>
                  Vidéo non disponible
                </div>
              )}

              {/* MODALE-TIMELINE-SCOPE-001 · LA QUESTION SE POSE ICI, parce
                  qu'elle ne se pose nulle part ailleurs.
                  Le bloc de choix de la page ne s'affiche que sur `peutAjouterVideo`,
                  donc sur ZERO fichier · un match qui a deja sa video n'avait
                  aucun moyen de declarer ce qu'elle contient. 14 matchs sont
                  dans ce cas au 11/08, dont celui du premier client payant.
                  Demander n'est pas deviner · on ne resout pas ce NULL. */}
              {!declarationFaite && (
                <div style={{ marginBottom: 20 }}>
                  <VideoScopeChoice sombre compact valeur={tlScope} onChange={setTlScope} />
                </div>
              )}

              {/* Marker rows · AUCUN tant que la question n'a pas de reponse ·
                  leur nombre en depend. */}
              {tlScope && !modaleSansMarqueurs && (<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                  {marqueursPour(tlScope).map(m => (
                    <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(255,255,255,0.06)` }}>
                      <div style={{ width: 3, height: 32, background: m.color, flexShrink: 0, borderRadius: 1 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(245,242,235,0.5)', marginBottom: 4 }}>{m.label}</div>
                        <div style={{ fontFamily: MONO, fontSize: 15, color: tlMarkers[m.key] != null ? CREAM : 'rgba(245,242,235,0.2)', letterSpacing: '.04em' }}>
                          {fmtTime(tlMarkers[m.key])}
                        </div>
                      </div>
                      <button onClick={() => poseMarkerModal(m.key)} style={{ padding: '6px 10px', background: m.color + '22', border: `1px solid ${m.color}55`, color: m.color, fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0 }}>
                        Poser
                      </button>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => adjustMarkerModal(m.key, -1)} style={{ width: 26, height: 26, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: CREAM, fontFamily: MONO, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                        <button onClick={() => adjustMarkerModal(m.key, +1)} style={{ width: 26, height: 26, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: CREAM, fontFamily: MONO, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                      </div>
                    </div>
                  ))}
                </div>

              </>)}

              {/* COULEURS-MAILLOT-001 · c'est ICI que se lance la découpe, donc
                  l'analyse : le serveur l'enchaîne tout seul à la fin du split
                  (`maybe_autostart_after_split`). Après ce bouton, il n'y a plus
                  de moment où poser les couleurs. */}
              <KitColorConfirm
                sombre
                compact
                couleurNous={tlKitNous}
                couleurAdverse={tlKitAdverse}
                onChangeNous={setTlKitNous}
                onChangeAdverse={setTlKitAdverse}
              />

              <div style={{ fontFamily: MONO, fontSize: 9, color: 'rgba(245,242,235,0.35)', margin: '20px 0', lineHeight: 1.6 }}>
                Positionnez la vidéo sur chaque événement clé, puis appuyez sur "Poser". Ces repères permettent à l'analyse de synchroniser les données de jeu avec les moments précis du match.
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setShowTimelineModal(false)}
                  style={{ flex: 1, padding: '11px', fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', background: 'transparent', border: '1px solid rgba(245,242,235,0.12)', color: 'rgba(245,242,235,0.4)', cursor: 'pointer' }}>
                  Annuler
                </button>
                {/* LE BOUTON GARDE LES DEUX CONDITIONS, pas seulement les
                    marqueurs · le commentaire ci-dessus le dit lui-meme, apres
                    ce bouton il n'y a plus de moment ou poser les couleurs.
                    Meme regle que `CreateMatch`, et la meme fonction · une
                    garde recopiee finit par diverger de celle qu'elle reflete. */}
                <button
                  onClick={handleValidateTimeline}
                  disabled={analyseLoading || !modalePrete}
                  style={{ flex: 2, padding: '11px', fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', background: modalePrete && !analyseLoading ? GOLD : 'rgba(79,142,247,0.2)', color: modalePrete && !analyseLoading ? D : 'rgba(245,242,235,0.3)', border: 'none', cursor: modalePrete && !analyseLoading ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
                  {analyseLoading ? 'Lancement...' : 'Valider et analyser'}
                </button>
              </div>

              {/* Skip */}
              <button
                onClick={handleCallAnalyze}
                disabled={analyseLoading}
                style={{ width: '100%', marginTop: 10, padding: '9px', fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', background: 'transparent', border: 'none', color: 'rgba(245,242,235,0.25)', cursor: 'pointer' }}>
                Passer et analyser sans marqueurs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retour */}
      <button data-print="hide" onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 20, padding: 0, transition: 'color .15s' }}
        onMouseEnter={e => e.currentTarget.style.color = GOLD}
        onMouseLeave={e => e.currentTarget.style.color = MUTED}>
        <ArrowLeft size={14} /> Retour aux matchs
      </button>

      {/* ── BANDEAU DU MATCH ──────────────────────────────────────────────
          Trois lignes, pas davantage · metadonnees et actions, score, contexte.

          IL FAISAIT 494 px POUR AFFICHER UN SCORE. Un titre « vs {adversaire} »
          en 52 px qui repetait ce que le bloc score disait deja, une bande beige
          hors charte pour le ressenti, et des soulignes pointilles sous chaque
          chiffre qui ne portaient aucune information.

          LES DEUX EQUIPES SONT NOMMEES. Le nom du club vient de NOTRE base
          (`user.club_name`), jamais du payload du prestataire : ici le score est
          saisi par le coach, il n'y a aucun risque d'attribution. C'est la
          difference avec le rapport d'analyse, qui dit « Nous » faute de pouvoir
          rattacher `equipe_1` a une equipe reelle (P0-BUTS-ATTRIBUTION-001).

          AUCUNE COULEUR EN DUR · tout vient de `theme.js`, prefixe `mh`. */}
      <div data-print="light" style={{ background: T.mhBg, animation: 'fadeUp .35s ease', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'relative', padding: isMobile ? '18px 20px' : '20px 32px' }}>

          {/* ── LIGNE 1 · metadonnees et actions ─────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
              <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: T.mhUs }}>{match.category}</span>
              {/* Competition et type fusionnes · « D3 · Championnat » etait
                  affiche deux fois, ici et plus bas. Une seule mention suffit. */}
              {editingType ? (
                <select value={match.type || ''} onChange={e => handleUpdateType(e.target.value)} disabled={savingType} autoFocus
                  onBlur={() => setEditingType(false)}
                  style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', background: 'transparent', border: `1px solid ${T.mhRule}`, color: T.mhInk, padding: '2px 6px', outline: 'none', cursor: 'pointer' }}>
                  {/* MINUSCULES, ET CE N'EST PAS COSMETIQUE · c'est ce que
                      l'API rend (`match.type.value`) et ce que CreateMatch.jsx
                      envoie deja. En MAJUSCULES, `value={match.type}` ne
                      correspondait a aucune option : le navigateur retombait
                      sur la premiere, un match Amical ouvrait son editeur sur
                      « Championnat », et un clic hors du champ suffisait a le
                      requalifier. */}
                  <option value="championnat">Championnat</option>
                  <option value="coupe">Coupe</option>
                  <option value="amical">Amical</option>
                </select>
              ) : (
                <span onClick={!isArchived ? () => setEditingType(true) : undefined}
                  style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: T.mhMuted, cursor: isArchived ? 'default' : 'pointer' }}>
                  {[match.competition, typeLabel(match.type)].filter(Boolean).join(' · ')}
                </span>
              )}
              <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: T.mhMuted }}>
                {new Date(match.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {match.season && match.season !== selectedSeason && (
                <span title={`Saison ${match.season}`} style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: T.mhUs, padding: '2px 7px', border: `1px solid ${T.mhRule}` }}>
                  {formatSeasonShort(match.season)}
                </span>
              )}
              <StatusBadge status={match.status} progress={match.progress} />
            </div>

            {/* Actions · bordees, discretes, meme traitement pour les trois */}
            <div data-print="hide" style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
              {aUneVideo && hasStudioAccess(user).allowed && (
                <button onClick={() => navigate(`/dashboard/matches/${matchId}/video`)} style={BTN_BANDEAU}>
                  <Film size={12} /> {!isMobile && 'Studio'}
                </button>
              )}
              {/* Lancer l'analyse.
                  RESTAURE APRES SUPPRESSION ACCIDENTELLE · ce bloc a disparu
                  dans bf60f9f6 (PR #227, refonte du bandeau), qui n'y touchait
                  pas volontairement. Le defaut est reste invisible trois jours ·
                  Studio continuait de s'afficher a cote, et rien ne couvrait ce
                  bouton. Un match avec video mais sans analyse n'avait donc plus
                  aucun moyen d'etre analyse. La fixture MatchDetailAAnalyser du
                  harnais existe pour que ca ne repasse pas.
                  `hasVid` reprend l'expression de `aUneVideo` sans la
                  reutiliser · la deduplication n'appartient pas a ce lot. */}
              {(() => {
                const isProcessingOrPending = match.status === 'PROCESSING' || match.status === 'processing' || match.status === 'PENDING' || match.status === 'pending'
                const hasVid = match.has_video || match.video_url || (match.video_url_mt1 && match.video_url_mt2)
                const alreadyAnalyzed = hasIaReport || match.ai_insights || match.status === 'ANALYZED' || match.status === 'analyzed'
                if (!hasVid || alreadyAnalyzed || isProcessingOrPending) return null
                // AUCUNE GARDE DE PLAN ICI, ET C'EST DELIBERE.
                //
                // Une garde `hasAnalysesAccess(user, …)` a existe une journee et
                // a ete retiree : elle lit `user.plan` BRUT, sans la resolution
                // de facturation. Or un coach membre de club porte un plan
                // personnel FREE et consomme le quota de son club · c'est
                // `get_billing_user` (backend/app/services/quota.py:104) qui
                // remonte au directeur sportif porteur du plan CLUB, en se
                // fondant sur club_id + absence de stripe_subscription_id +
                // categorie geree. Le frontend ne fait rien de tout cela · la
                // garde bloquait donc TOUS les coachs de club, qui sont des
                // clients payants.
                //
                // L'autorite reste le serveur, qui lui resout correctement :
                // check_quota_available avant soumission, consume_quota au
                // decompte, tous deux passant par get_billing_user. Un bouton
                // visible a tort coute un aller-retour et un message clair ; un
                // bouton masque a tort coute un client qui croit le produit
                // casse.
                return (
                  <button onClick={handleLancerAnalyse} disabled={analyseLoading}
                    title="Lancer l'analyse IA de ce match"
                    style={{ ...BTN_BANDEAU, cursor: analyseLoading ? 'wait' : 'pointer' }}>
                    {analyseLoading
                      ? <><Loader size={12} style={{ animation: 'spin .7s linear infinite' }} />{!isMobile && ' Lancement...'}</>
                      : <><Cpu size={12} />{!isMobile && ' Analyser'}</>}
                  </button>
                )
              })()}
              {/* Le bouton reste VISIBLE et desactive plutot que masque : son
                  `title` explique ce qu'il manque. Un bouton absent ne dit rien. */}
              {!isArchived && (
                <button onClick={() => setSendModalOpen(true)} disabled={!hasLineup}
                  title={!hasLineup ? "Saisis la composition avant d'envoyer les cartes" : 'Envoyer les cartes joueurs'}
                  style={{ ...BTN_BANDEAU, opacity: hasLineup ? 1 : 0.4, cursor: hasLineup ? 'pointer' : 'not-allowed' }}>
                  <Send size={12} /> {!isMobile && 'Cartes'}
                </button>
              )}
              {/* CONDITIONNE AU FLAG SERVEUR, pas seulement a la presence d'un
                  rapport · `ENABLE_REPORT_PDF` est desactive par defaut cote
                  backend, et la route repond alors 404. Sans cette condition,
                  un coach cliquerait sur un bouton qui echoue toujours. */}
              {hasIaReport && match.report_pdf_available && (
                <button
                  onClick={handleDownloadPDF}
                  disabled={pdfEnCours}
                  title={pdfEnCours ? 'Génération du rapport en cours…' : 'Télécharger le rapport en PDF'}
                  style={{ ...BTN_BANDEAU, opacity: pdfEnCours ? 0.5 : 1, cursor: pdfEnCours ? 'wait' : 'pointer' }}>
                  <Download size={12} /> {!isMobile && (pdfEnCours ? 'Génération…' : 'PDF')}
                </button>
              )}
            </div>
          </div>

          {/* ── LIGNE 2 · le score ────────────────────────────────────────
              Grille en trois colonnes sur bureau, empilee sur mobile. Les deux
              chiffres ont la MEME taille : l'un est le notre, l'autre celui de
              l'adversaire, aucun des deux n'est plus important.
              Sur mobile la grille passe en une colonne plutot que de reduire la
              police : un nom de club illisible ne vaut pas mieux qu'un nom
              absent. */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) auto minmax(0,1fr)',
            alignItems: 'center', gap: isMobile ? 10 : 20,
            padding: isMobile ? '4px 0 14px' : '6px 0 16px',
          }}>
            <EquipeNom nom={nomClub} lieu={isHome ? 'Domicile' : 'Extérieur'} align={isMobile ? 'center' : 'right'} isMobile={isMobile} />

            <div style={{ textAlign: 'center' }}>
              {editingScore ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  {/* CHAQUE CHAMP PORTE LE NOM DE SON EQUIPE. Le bandeau est
                      oriente club, donc le champ de gauche est le notre — et
                      il le DIT, au lieu de le laisser deviner par la position
                      (P0-SCORE-ECRITURE-001). */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, justifyContent: 'center' }}>
                    <div>
                      <div style={LBL_EQUIPE} title={nomClub}>{nomClub}</div>
                      <input type="number" min="0" max="30" value={scoreUsEdit}
                        aria-label={`Score de ${nomClub}`}
                        onChange={e => setScoreUsEdit(e.target.value)} autoFocus style={INPUT_SCORE} />
                    </div>
                    <span style={{ fontFamily: DISP, fontSize: 34, color: T.mhMuted, paddingBottom: 2 }}>–</span>
                    <div>
                      <div style={LBL_EQUIPE} title={match.opponent}>{match.opponent}</div>
                      <input type="number" min="0" max="30" value={scoreThemEdit}
                        aria-label={`Score de ${match.opponent}`}
                        onChange={e => setScoreThemEdit(e.target.value)} style={INPUT_SCORE} />
                    </div>
                    <button onClick={handleSaveScore} disabled={savingScore} style={{ ...BTN_BANDEAU, padding: '7px 12px' }}>
                      {savingScore ? '...' : '✓'}
                    </button>
                    <button onClick={() => setEditingScore(false)} style={{ ...BTN_BANDEAU, padding: '7px 12px' }}>✕</button>
                  </div>

                  {/* La mi-temps s'edite ICI ou nulle part. Le plein temps
                      seul modifiable laissait une MT1 3-0 sous un 1-4. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.14em', color: T.mhMuted }}>MT1</span>
                    <input type="number" min="0" max="30" value={scoreUsHtEdit}
                      aria-label={`Score de ${nomClub} à la mi-temps`}
                      onChange={e => setScoreUsHtEdit(e.target.value)} placeholder="—" style={INPUT_SCORE_HT} />
                    <span style={{ fontFamily: MONO, fontSize: 12, color: T.mhMuted }}>–</span>
                    <input type="number" min="0" max="30" value={scoreThemHtEdit}
                      aria-label={`Score de ${match.opponent} à la mi-temps`}
                      onChange={e => setScoreThemHtEdit(e.target.value)} placeholder="—" style={INPUT_SCORE_HT} />
                    <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', color: T.mhMuted, textTransform: 'uppercase' }}>
                      optionnel
                    </span>
                  </div>
                </div>
              ) : (
                <div onClick={!isArchived ? openScoreEdit : undefined}
                  style={{ cursor: isArchived ? 'default' : 'pointer', display: 'inline-block' }}
                  title={isArchived ? undefined : 'Modifier le score'}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, justifyContent: 'center' }}>
                    {/* Le score du club garde le bleu charte a l'impression :
                        c'est un chiffre, pas un aplat, il coute une goutte
                        d'encre et il porte l'identite de l'equipe. */}
                    <span data-print="keep-color" style={{ fontFamily: DISP, fontSize: isMobile ? 46 : 56, fontWeight: 800, lineHeight: 1, color: T.mhUs }}>
                      {hasScore ? scoreUs : '–'}
                    </span>
                    <span style={{ fontFamily: DISP, fontSize: isMobile ? 26 : 30, color: T.mhMuted, lineHeight: 1 }}>–</span>
                    <span style={{ fontFamily: DISP, fontSize: isMobile ? 46 : 56, fontWeight: 800, lineHeight: 1, color: T.mhInk }}>
                      {hasScore ? scoreThem : '–'}
                    </span>
                  </div>
                  {hasScore && (
                    <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: T.mhMuted, marginTop: 6 }}>
                      {resultLabel}
                    </div>
                  )}
                  {scoreUsHt !== null && (
                    <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', color: T.mhMuted, marginTop: 3 }}>
                      MT1 · {scoreUsHt} – {scoreThemHt}
                    </div>
                  )}
                </div>
              )}
            </div>

            <EquipeNom nom={match.opponent} lieu={isHome ? 'Extérieur' : 'Domicile'} align={isMobile ? 'center' : 'left'} isMobile={isMobile} />
          </div>

          {/* ── LIGNE 3 · contexte ────────────────────────────────────────
              Meteo, terrain et formation sur UNE ligne, separee par un filet.
              La bande beige `CREAM` qui portait le ressenti a disparu : c'etait
              la seule surface hors charte de la page.

              LE RESSENTI ET SA NOTE SONT SORTIS DE CETTE LIGNE (25/08/2026).
              Ils la partageaient, et la note y etait tenue sur une seule ligne
              par `white-space: nowrap` + `overflow: hidden`. Mesure du jour sur
              la note la plus longue en production, 560 caracteres · 28 % lisible
              a l'ecran, 15 % dans le PDF A4. Le reste n'existait que dans
              l'attribut `title`, c'est-a-dire au survol · donc nulle part sur
              papier, alors que le PDF est rendu depuis cette page meme.

              La ligne de contexte ne s'affiche que si elle a quelque chose a
              dire · sans elle, le filet et son espacement restaient seuls. */}
          <div style={{ borderTop: `1px solid ${T.mhRule}`, paddingTop: 12 }}>
            {(match.weather || match.pitch_type || match.formation) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
                {match.weather && <ContexteItem label="Météo" valeur={match.weather} />}
                {match.pitch_type && <ContexteItem label="Terrain" valeur={match.pitch_type} />}
                {match.formation && <ContexteItem label="Formation" valeur={match.formation} />}
              </div>
            )}

            {editingFeeling ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 260 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {FEELINGS.map(f => (
                    <button key={f.id} onClick={() => setFeelingEdit(f.id)} style={{
                      padding: '5px 12px', fontFamily: MONO, fontSize: 9, letterSpacing: '.08em',
                      background: 'transparent',
                      color: feelingEdit === f.id ? T.mhInk : T.mhMuted,
                      border: `1px solid ${feelingEdit === f.id ? T.mhInk : T.mhRule}`,
                      cursor: 'pointer',
                    }}>{f.label}</button>
                  ))}
                </div>
                <textarea value={commentEdit} onChange={e => setCommentEdit(e.target.value)}
                  placeholder="Commentaire sur le match..." rows={2}
                  style={{ width: '100%', fontFamily: MONO, fontSize: 11, border: `1px solid ${T.mhRule}`, background: 'transparent', padding: '7px 10px', color: T.mhInk, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setEditingFeeling(false)} style={BTN_BANDEAU}>Annuler</button>
                  <button onClick={handleSaveFeeling} disabled={savingFeeling || !feelingEdit} style={BTN_BANDEAU}>
                    {savingFeeling ? '...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            ) : (
              /* RESSENTI ET NOTE · leur propre bloc, sur toute la largeur.
                 `alignItems: baseline` aligne le libelle, le ressenti et la
                 premiere ligne de la note sur la meme assise. */
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.mhUs, flexShrink: 0 }}>Ressenti</span>
                {match.coach_feeling ? (
                  <span style={{ fontFamily: MONO, fontSize: 11, color: T.mhInk, flexShrink: 0 }}>{feelingLabel(match.coach_feeling)}</span>
                ) : (
                  <span style={{ fontFamily: MONO, fontSize: 11, color: T.mhMuted, fontStyle: 'italic', flexShrink: 0 }}>Non renseigné</span>
                )}
                {match.coach_comment && (
                  /* NI TRONCATURE NI PLAFOND DE HAUTEUR · la note s'affiche en
                     entier, a l'ecran comme sur papier.

                     `pre-wrap` PLUTOT QUE `normal` · le coach ecrit dans un
                     `<textarea>`, ou il peut aller a la ligne, et la base
                     stocke ses `\n`. `nowrap` les reduisait a une espace ·
                     « bon bloc defensif ⏎ transitions a ameliorer » sortait sur
                     une seule ligne, la structure effacee. `pre-wrap` garde les
                     retours voulus ET replie les lignes trop longues.

                     `flex: 1 1 240px` · la note se pose apres le ressenti quand
                     il reste de la place, et prend sa propre ligne des qu'elle
                     n'en a plus. `minWidth: 0` autorise ce repli.

                     PAS DE `title` · il n'y a plus rien a reveler au survol, et
                     un survol ne s'imprime pas. */
                  <span style={{ fontFamily: MONO, fontSize: 11, color: T.mhMuted, fontStyle: 'italic', whiteSpace: 'pre-wrap', flex: '1 1 240px', minWidth: 0, lineHeight: 1.6 }}>
                    — {match.coach_comment}
                  </span>
                )}
                {!isArchived && (
                  <button data-print="hide" onClick={() => { setFeelingEdit(match.coach_feeling || ''); setCommentEdit(match.coach_comment || ''); setEditingFeeling(true) }}
                    style={{ ...BTN_BANDEAU, marginLeft: 'auto', alignSelf: 'flex-start', flexShrink: 0 }}>
                    ✎ {!isMobile && 'Modifier'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── PARCOURS-VIDEO-DECLARE-001 · reprise sur seconde période ── */}
      {attendSecondePeriode && (
        <div data-print="hide" style={{
          marginTop: 16, padding: '16px 18px',
          background: GOLD_BG, border: `1px solid ${GOLD_BDR}`, borderLeft: `2px solid ${GOLD}`,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <Upload size={16} color={GOLD} style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 200, flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD, marginBottom: 4 }}>
              Ta première mi-temps est arrivée
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.6 }}>
              Elle est enregistrée, tu n'auras jamais à la renvoyer. Ajoute la
              seconde, ou analyse cette période seule.
            </div>
          </div>
          <label style={{
            padding: '11px 18px', background: GOLD, color: CREAM, border: 'none',
            fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
            textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0,
          }}>
            <input type="file" accept=".mp4,.mov,.m4v,video/mp4,video/quicktime"
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                if (!f) return
                const invalide = verifierFichierVideo(f)
                if (invalide) { setErreurVideo(invalide); return }
                setErreurVideo('')
                handleEnvoyerVideo(f)
              }}
              style={{ display: 'none' }} />
            {envoiVideoEnCours ? `Envoi ${progressionVideo}%` : 'Ajouter la 2e période'}
          </label>
          <button onClick={handleLancerAnalyse} disabled={analyseLoading}
            style={{
              padding: '11px 18px', background: 'transparent', border: `1px solid ${GOLD_BDR}`,
              color: GOLD, fontFamily: MONO, fontSize: 10, letterSpacing: '.1em',
              textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0,
            }}>
            Analyser cette période
          </button>
        </div>
      )}

      {/* ── PARCOURS-MITEMPS-UNIQUE-001 · bandeau « vidéo à préparer » ──
          UN MATCH `pending` AVEC UNE VIDÉO N'EST PAS UN ÉTAT NEUTRE · c'est un
          travail en attente, et jusqu'ici la page n'en disait RIEN.

          C'est le défaut qui a coûté Martin Hiblot, premier client payant · le
          10/08 il uploade 3,9 Go, atterrit ici, tourne en rond pendant deux
          minutes, appelle trois fois son rapport (404, 404, 404) et repart.
          Sa vidéo était bien en base · seul l'écran se taisait.

          TROIS CHEMINS produisent cet état, et ils atterrissent TOUS ici · le
          bouton « Finir plus tard » du wizard, une page quittée à l'étape des
          marqueurs, et le bloc « Ajouter la vidéo du match » de cette page.
          Un seul bandeau sur la destination les couvre tous les trois.

          Mesuré le 11/08 · 9 matchs sur 70 sont dans cet état, répartis sur 5
          clubs, dont un créé le jour même. Ce n'est pas un cas isolé.

          `pending` reste EXCLU de `DECOUPE_EN_COURS` juste au-dessus, et c'est
          volontaire · rien ne tourne, donc rien à suivre. Ce sont deux
          questions distinctes · « un traitement tourne-t-il ? » et « le coach
          a-t-il quelque chose à faire ? ». */}
      {videoAPreparer && (
        <div data-print="hide" style={{
          marginTop: 16, padding: '16px 18px',
          background: GOLD_BG, border: `1px solid ${GOLD_BDR}`, borderLeft: `2px solid ${GOLD}`,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <Upload size={16} color={GOLD} style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 200, flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD, marginBottom: 4 }}>
              Ta vidéo est bien arrivée
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.6 }}>
              {!analyseBloquee
                ? "Il reste une étape avant l'analyse : placer les repères du match sur la vidéo. Sans eux, le traitement ne démarre pas."
                : aDroitALEssai
                  ? "Démarre ton essai pour l'analyser, 7 jours à 0 €."
                  /* LE REPLI NEUTRE N'EST PAS DECORATIF · promettre « 7 jours à
                     0 € » a quelqu'un qui a deja consomme son essai serait le
                     defaut qu'on corrige, deplace d'un cran. Aucun compte n'est
                     dans ce cas aujourd'hui — la variante existe AVANT que le
                     cas arrive. */
                  : "Il te faut un abonnement pour lancer l'analyse."}
            </div>
          </div>
          {/* LE BOUTON MENE OU IL DIT. `#abonnement` est une ancre reelle sur
              la page de reglages · sans elle, le coach arrivait sous six blocs
              sans rapport avec ce qu'il venait chercher. */}
          <button
            onClick={analyseBloquee
              ? () => navigate('/dashboard/settings#abonnement')
              : handleLancerAnalyse}
            disabled={analyseLoading}
            style={{
              padding: '11px 20px', background: GOLD, color: CREAM, border: 'none',
              fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase', cursor: analyseLoading ? 'wait' : 'pointer', flexShrink: 0,
            }}>
            {analyseBloquee
              ? (aDroitALEssai ? "Démarrer l'essai →" : 'Voir les offres →')
              : (analyseLoading ? 'Ouverture…' : 'Placer les repères →')}
          </button>
        </div>
      )}

      {/* ── P1-SPLIT-MATCHDETAIL-001 · bandeau de decoupe ──────────────
          Obligatoire par construction depuis que l'upload delegue le suivi a
          cette page : sans lui, un coach arrive ici apres plusieurs minutes
          d'upload et trouve un ecran muet sur un match sans video jouable.
          Il dit ce qui se passe et disparait quand c'est fini · le detail du
          traitement (progression, boutons) reste a P1-UX-SPLIT-001. */}
      {((decoupeEnCours && splitPhase === null) || splitPhase === 'failed') && (
        <div data-print="hide" style={{
          marginTop: 16, padding: '14px 18px',
          background: splitPhase === 'failed' ? 'rgba(239,68,68,0.06)' : GOLD_BG,
          border: `1px solid ${splitPhase === 'failed' ? 'rgba(239,68,68,0.25)' : GOLD_BDR}`,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          {splitPhase === 'failed'
            ? <AlertCircle size={16} color={RED} style={{ flexShrink: 0 }} />
            : <Loader size={16} color={GOLD} style={{ flexShrink: 0, animation: 'spin .8s linear infinite' }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: splitPhase === 'failed' ? RED : GOLD, marginBottom: 4 }}>
              {splitPhase === 'failed' ? 'Découpe interrompue' : (splitMessage || libelleDecoupe('splitting'))}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.6 }}>
              {splitPhase === 'failed'
                ? splitMessage
                : "Le traitement tourne sur nos serveurs : tu peux fermer cette page, il continue sans toi et l'analyse s'enchaîne toute seule."}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div data-print="hide" style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${RULE}`, marginTop: 24, marginBottom: 20, overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => changerOnglet(tab.id)} style={{
            padding: isMobile ? '10px 14px' : '12px 20px', fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
            background: 'transparent', border: 'none',
            borderBottom: ongletActif === tab.id ? `2px solid ${GOLD}` : '2px solid transparent', marginBottom: -2,
            color: ongletActif === tab.id ? GOLD : MUTED, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {ongletActif === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, animation: 'fadeUp .3s ease' }}>

          {/* Événements — éditables */}
          <Card accentColor={GOLD_BDR}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${RULE}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 14, height: 1, background: GOLD, display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD }}>Événements</span>
              </div>
              <button onClick={() => setShowEventModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'transparent', border: `1px solid ${RULE}`, color: MUTED, fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = RULE; e.currentTarget.style.color = MUTED }}>
                <Plus size={10} /> Ajouter
              </button>
            </div>
            {(!match.events || match.events.length === 0) ? (
              <div style={{ padding: '20px', fontFamily: MONO, fontSize: 10, color: MUTED, textAlign: 'center' }}>Aucun événement. Ajoute les buts, passes décisives et cartons.</div>
            ) : (
              match.events.map((ev, i) => (
                <div key={ev.id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', borderBottom: i < match.events.length - 1 ? `1px solid ${RULE}` : 'none' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: (['goal','assist','penalty_scored'].includes(ev.event_type) ? ev.event_type === 'assist' ? BLUE : RED : ev.event_type === 'yellow' ? ORANGE : MUTED), flexShrink: 0 }} />
                  <EventBadge type={ev.event_type} />
                  <span style={{ fontFamily: MONO, fontSize: 11, color: INK, flex: 1 }}>{ev.player_name || playersById.get(ev.player_id)?.name || ''}</span>
                  {ev.minute && <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>{ev.minute}'</span>}
                  <button onClick={() => handleDeleteEvent(ev.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, padding: '2px 6px', flexShrink: 0, lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = RED}
                    onMouseLeave={e => e.currentTarget.style.color = MUTED}>
                    <X size={12} />
                  </button>
                </div>
              ))
            )}
          </Card>

          {/* Composition — vue ou CTA saisie */}
          {hasLineup ? (
            <LineupView
              lineup={lineupArray}
              events={match.events || []}
              formation={match.formation}
              isMobile={isMobile}
              onEdit={!isArchived ? () => setShowLineupModal(true) : undefined}
            />
          ) : (
            <Card accentColor={RULE} style={{ padding: '28px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 40, height: 40, background: GOLD_BG, border: `1px solid ${GOLD_BDR}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Users size={18} color={GOLD} />
                </div>
                <div>
                  <div style={{ fontFamily: DISP, fontSize: 16, textTransform: 'uppercase', color: INK, marginBottom: 4 }}>Composition non renseignée</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>Qui a joué ce match ? Saisie rapide depuis l'effectif.</div>
                </div>
              </div>
              {!isArchived && (
                <button onClick={() => setShowLineupModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px', background: D, color: CREAM, fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
                  <Plus size={12} /> Saisir la composition
                </button>
              )}
            </Card>
          )}

          {/* Vidéo — CTA d'ajout sur un match saisi sans vidéo.
              Même dessin que la carte Composition ci-dessus : c'est le motif de
              cet écran pour « il manque quelque chose, voici comment le
              donner ». */}
          {/* PARCOURS-VIDEO-DECLARE-002 · LA PAGE RESPECTE LA DECLARATION.
              Un match declare « deux fichiers » ou « une periode » recoit ses
              emplacements nommes, pas le champ generique unique. */}
          {/* ── PARCOURS-VIDEO-DECLARE-004 · LE CHOIX D'ABORD, L'ENVOI ENSUITE ──
              L'ordre etait inverse · on demandait un fichier AVANT que le coach
              ait dit ce qu'il avait filme, et le choix qui conditionne tout
              etait replie derriere un lien souligne en petit corps.

              Le wizard fait l'inverse, et c'est le bon sens · declarer, puis
              envoyer. Un seul chemin.

              LE CHOIX REMPLACE L'ENTREE REPLIEE de frontend#265 · le besoin
              qu'elle couvrait — changer d'avis tant que rien n'est arrive — est
              satisfait par construction, puisque le choix reste affiche et
              modifiable tant que `peutAjouterVideo` est vrai.

              `match.video_scope` est lu BRUT ici, jamais `scopeDuMatch` · ce
              dernier resout NULL en `full_match`, ce qui afficherait une zone
              d'upload sur une question a laquelle personne n'a repondu. C'est
              exactement le defaut qu'on corrige. */}
          {peutAjouterVideo && (
            <Card accentColor={GOLD_BDR} style={{ padding: '24px' }}>
              <div style={{ fontFamily: DISP, fontSize: 16, textTransform: 'uppercase', color: INK, marginBottom: 14 }}>
                Ajouter la vidéo du match
              </div>

              <VideoScopeChoice
                compact
                valeur={declarationFaite ? match.video_scope : null}
                onChange={(v) => {
                  if (v === match.video_scope) return
                  // LES REPERES SUIVENT LA DECLARATION (conserve de #265).
                  //
                  // Sur une periode unique, « fin MT1 » et « coup d'envoi MT2 »
                  // N'EXISTENT PAS. Quatre reperes non nuls sont exactement ce
                  // que `recovery.py:142` exige pour relancer une decoupe · la
                  // reprise irait decouper un fichier qui n'a rien a decouper.
                  const corps = { video_scope: v }
                  if (v === 'single_half') {
                    corps.timeline_half_time = null
                    corps.timeline_kick_off_2 = null
                  }
                  fetch(`${API}/matches/${matchId}/`, {
                    method: 'PATCH',
                    headers: authHeaders(),
                    body: JSON.stringify(corps),
                  })
                    .then(r => (r.ok ? r.json() : null))
                    .then(frais => {
                      if (frais) setMatch(prev => ({ ...prev, ...frais }))
                      else showToast("Impossible d'enregistrer ce choix", 'error')
                    })
                    .catch(() => showToast('Erreur réseau', 'error'))
                  // Un fichier choisi pour un emplacement qui n'existe plus doit
                  // etre relache · on n'enverrait pas une 2e periode sur un
                  // match qui n'en attend plus.
                  setFichiersPeriodes([])
                }}
              />

              {/* AUCUNE ZONE TANT QUE LA QUESTION N'A PAS DE REPONSE. */}
              {declarationFaite && (
                <>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, margin: '4px 0 14px', lineHeight: 1.6 }}>
                    {match.video_scope === 'two_files'
                      ? "Tu peux n'en envoyer qu'une maintenant et revenir plus tard pour l'autre. Rien ne se perd."
                      : "MP4, MOV ou M4V, jusqu'à 8 Go. Le carnet déjà saisi est conservé."}
                  </div>
                  <VideoDropSlots
                    scope={match.video_scope}
                    fichiers={fichiersPeriodes}
                    dejaEnvoyes={{ 0: Boolean(match.video_url_mt1), 1: Boolean(match.video_url_mt2) }}
                    disabled={envoiVideoEnCours}
                    onChoisir={(i, f) => {
                      const invalide = verifierFichierVideo(f)
                      if (invalide) { setErreurVideo(invalide); return }
                      setErreurVideo('')
                      setFichiersPeriodes(prev => { const s2 = [...prev]; s2[i] = f; return s2 })
                    }}
                    onRetirer={(i) => setFichiersPeriodes(prev => { const s2 = [...prev]; s2[i] = null; return s2 })}
                  />
                  {fichiersPeriodes.some(Boolean) && (
                    <button onClick={envoyerPeriodes} disabled={envoiVideoEnCours}
                      style={{ padding: '11px 22px', background: GOLD, color: CREAM, border: 'none', fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', cursor: envoiVideoEnCours ? 'wait' : 'pointer' }}>
                      {envoiVideoEnCours ? `Envoi ${progressionVideo}%` : 'Envoyer →'}
                    </button>
                  )}
                </>
              )}

              {erreurVideo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: `1px solid rgba(239,68,68,0.25)` }}>
                  <AlertCircle size={12} color={RED} style={{ flexShrink: 0 }} />
                  <span style={{ fontFamily: MONO, fontSize: 10, color: RED }}>{erreurVideo}</span>
                </div>
              )}
            </Card>
          )}

          {/* Le bloc « Statistiques collectives » a ete retire d'ici. Il tirait
              ses huit lignes de match.stats et faisait doublon avec l'onglet
              Rapport, qui affiche les memes chiffres, par periode, et bien
              davantage. La Vue d'ensemble garde ce que le Rapport ne porte pas :
              evenements, composition, ressenti.

              match.stats reste lu ailleurs — l'empty state plus bas, et surtout
              les sparklines de DashboardHome, qui lisent la valeur servie par
              l'API et non celle-ci. */}

          {/* PROMESSES-ANALYSE-001 · le bloc « Heatmap collective » a ete
              RETIRE. Sa garde `match.analysis_data?.heatmap` n'a jamais ete
              vraie · mesure sur les 7 matchs analyses de la base, `analysis_data`
              ne porte que `source` et les trois `result_key_*`. Le bloc
              `cartographies` dont viendrait la heatmap est absent de TOUS les
              payloads du prestataire. */}

          {/* Empty state */}
          {!match.stats && !match.analysis_data && (match.status === 'COMPLETED' || match.status === 'completed') && (
            <div style={{ background: '#fff', border: `1px solid ${RULE}`, borderTop: `2px solid ${GOLD_BDR}`, padding: '32px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: DISP, fontSize: 18, textTransform: 'uppercase', color: INK, marginBottom: 8 }}>Carnet complété</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.8 }}>Ajoute une vidéo pour obtenir les statistiques collectives IA.</div>
            </div>
          )}

          {/* DRAFT */}
          {match.status === 'DRAFT' && (
            <Card accentColor={GOLD_BDR} style={{ padding: '28px 24px', textAlign: 'center' }}>
              <BookOpen size={24} color={GOLD} style={{ marginBottom: 10 }} />
              <div style={{ fontFamily: DISP, fontSize: 20, textTransform: 'uppercase', color: INK, marginBottom: 8 }}>Carnet en cours</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginBottom: 16 }}>Ce match est en brouillon. Complète le score.</div>
              <button onClick={() => navigate(`/dashboard/matches/new?resume=${match.id}`)} style={{ padding: '11px 24px', background: GOLD, color: D, fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                Compléter le carnet
              </button>
            </Card>
          )}

          {/* PENDING */}
          {(match.status === 'PENDING' || match.status === 'pending') && (
            <Card accentColor={GOLD} style={{ padding: '28px 24px', textAlign: 'center' }}>
              <Clock size={24} color={GOLD} style={{ marginBottom: 10 }} />
              <div style={{ fontFamily: DISP, fontSize: 20, textTransform: 'uppercase', color: INK, marginBottom: 8 }}>Analyse en attente</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>Vidéo reçue. L'analyse démarrera automatiquement.</div>
            </Card>
          )}

          {/* PROCESSING */}
          {(match.status === 'PROCESSING' || match.status === 'processing') && (
            <Card accentColor={BLUE} style={{ padding: '28px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: DISP, fontSize: 20, textTransform: 'uppercase', color: INK, marginBottom: 12 }}>Analyse en cours</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginBottom: 16, lineHeight: 1.6 }}>
                Chaque mi-temps est analysée séparément (2 à 6 min typiques). Tu peux quitter cette page — les résultats apparaîtront à la reconnexion.
              </div>
              {(() => {
                const a = analysisStatus || match.analysis || {}
                const mt1s = a.mt1?.status || a.status || 'queued'
                const mt2s = a.mt2?.status || a.status || 'queued'
                const label = (s) => ({
                  queued: 'En file', running: 'En cours', succeeded: 'Terminée',
                  failed: 'Échec', none: '—', partial: 'Partiel',
                }[s] || s)
                const color = (s) => ({
                  succeeded: GREEN, failed: RED, running: BLUE, queued: MUTED,
                }[s] || MUTED)
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 360, margin: '0 auto' }}>
                    {[
                      { label: 'MT1', status: mt1s },
                      { label: 'MT2', status: mt2s },
                    ].map((row) => (
                      <div key={row.label} style={{ padding: '12px 14px', border: `1px solid ${RULE}`, background: CREAM }}>
                        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>{row.label}</div>
                        <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: color(row.status) }}>{label(row.status)}</div>
                      </div>
                    ))}
                  </div>
                )
              })()}
              {(match.status === 'ERROR' || analysisStatus?.status === 'failed') && (
                <div style={{ marginTop: 16, fontFamily: MONO, fontSize: 10, color: RED }}>
                  {analysisStatus?.error || match.error_message || 'Analyse échouée.'}
                </div>
              )}
            </Card>
          )}

          {/* ERROR */}
          {(match.status === 'ERROR' || match.status === 'error') && (
            <Card accentColor={RED} style={{ padding: '28px 24px', textAlign: 'center' }}>
              <AlertCircle size={24} color={RED} style={{ marginBottom: 10 }} />
              <div style={{ fontFamily: DISP, fontSize: 20, textTransform: 'uppercase', color: INK, marginBottom: 8 }}>Analyse échouée</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginBottom: 16 }}>
                {match.error_message || analysisStatus?.error || 'Une erreur est survenue pendant l\'analyse.'}
              </div>
              <button
                type="button"
                disabled={analyseLoading}
                onClick={handleLancerAnalyse}
                style={{ padding: '11px 24px', background: GOLD, color: D, fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, border: 'none', cursor: 'pointer' }}
              >
                Relancer l'analyse
              </button>
              {/* DIRE CE QUI VA ETRE RELANCE, ET CE QUI EST GARDE.
                  Le bouton disait « Relancer l'analyse » sans preciser sur
                  quoi. Le 08/08/2026, un coach dont la 2e periode etait
                  analysee a clique dessus et les deux sont reparties : il a
                  perdu un rapport qu'il avait. La liste vient du SERVEUR
                  (`analysis_relance_prevue`), qui seul connait les cles de
                  resultat. Ne pas la recalculer ici. */}
              {texteRelance && (
                <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginTop: 12, lineHeight: 1.6 }}>
                  {texteRelance}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ── RAPPORT IA (AnalyseResult MT1/MT2) ── */}
      {ongletActif === 'ia' && (
        <Card accentColor={GOLD} style={{ padding: '24px 20px' }}>
          {/* `team1Label` / `team2Label` NE SONT PLUS PASSES. Ils injectaient
              le nom du club du coach sur `equipe_1`, dont l'attribution n'est
              pas ancree : le rapport pouvait donc afficher les statistiques de
              l'adversaire sous le nom du club qui paie. Le composant impose
              desormais « Nous » et « Adversaire » (cf. son en-tete de fichier).

              Le score vient d'ICI et non du payload d'analyse : c'est celui
              saisi par le coach (STATS-002). L'orientation passe par
              `scoresUsThem` — `score_home` n'est le notre que si l'on
              recoit (cf. src/utils/matchResult.js). */}
          <MatchAnalyseReport
            periodeInitiale={SLUGS_PERIODE[searchParams.get('periode')] || null}
            mt1={analyseResults.mt1}
            mt2={analyseResults.mt2}
            full={analyseResults.full}
            category={match.category}
            competition={match.competition}
            matchDate={match.date}
            scoreUs={scoreUs}
            scoreThem={scoreThem}
            loading={analyseResultsLoading}
            error={analyseResultsError}
          />
        </Card>
      )}

      {/* ── JOUEURS ── */}
      {ongletActif === 'players' && (hasLineup || match.player_stats) && (() => {
        const hasVideo = !!match.player_stats

        // Construire les stats par joueur depuis lineup + events
        const evByPlayer = {}
        ;(match.events || []).forEach(ev => {
          if (!evByPlayer[ev.player_id]) evByPlayer[ev.player_id] = []
          evByPlayer[ev.player_id].push(ev)
        })

        // Fusionner lineup avec player_stats si dispo
        const rows = lineupArray.map(p => {
          const evs = evByPlayer[p.player_id] || []
          const goals   = evs.filter(e => e.event_type === 'goal' || e.event_type === 'penalty_scored').length
          const assists  = evs.filter(e => e.event_type === 'assist').length
          const yellows  = evs.filter(e => e.event_type === 'yellow').length
          const reds     = evs.filter(e => e.event_type === 'red').length
          // Minutes jouées
          let minutes = 90
          if (!p.starter) minutes = p.subbed_in ? 90 - p.subbed_in : 0
          if (p.subbed_off) minutes = p.subbed_off

          // Stats vidéo si disponibles
          const ps = hasVideo ? (match.player_stats || []).find(s => s.player_id === p.player_id || s.name === p.name) : null

          return { ...p, goals, assists, yellows, reds, minutes, ps }
        })

        // Colonnes base (toujours) + vidéo (si player_stats)
        const colsBase = isMobile
          ? ['Joueur', 'Min', 'Buts', 'PD']
          : ['Joueur', 'Min', 'Buts', 'PD', 'J', 'R']
        const colsVideo = hasVideo
          ? (isMobile ? ['Passes', 'Km'] : ['Passes', 'Tirs', 'Duels', 'Km'])
          : []
        const cols = [...colsBase, ...colsVideo]
        const gridCols = `1fr ${cols.slice(1).map(() => isMobile ? '44px' : '60px').join(' ')}`

        return (
          <div style={{ animation: 'fadeUp .3s ease' }}>
            <Card accentColor={GOLD_BDR}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${RULE}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 14, height: 1, background: GOLD, display: 'inline-block' }} />
                  <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD }}>Performances du match</span>
                </div>
                {!hasVideo && (
                  <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED, padding: '3px 10px', border: `1px solid ${RULE}` }}>
                    + stats vidéo disponibles avec analyse
                  </span>
                )}
              </div>

              {/* Titulaires */}
              <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, padding: '8px 20px', background: CREAM }}>Titulaires</div>
              {/* Colonnes header */}
              <div style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '8px 20px', background: CREAM, borderBottom: `1px solid ${RULE}` }}>
                {cols.map(h => (
                  <span key={h} style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED, textAlign: h === 'Joueur' ? 'left' : 'center' }}>{h}</span>
                ))}
              </div>

              {rows.filter(p => p.starter).map((p, i) => (
                <div key={p.player_id || i} style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '11px 20px', alignItems: 'center', borderBottom: `1px solid ${RULE}`, background: i % 2 === 0 ? '#fff' : 'rgba(245,242,235,0.4)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: getPosColor(p.position) + '18', border: `1px solid ${getPosColor(p.position)}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: DISP, fontSize: 12, color: getPosColor(p.position) }}>{p.number}</span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: MONO, fontSize: 11, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: MUTED, letterSpacing: '.06em' }}>{p.position}</div>
                    </div>
                  </div>
                  <span style={{ fontFamily: DISP, fontSize: 13, color: INK, textAlign: 'center' }}>{p.minutes}'</span>
                  <span style={{ fontFamily: DISP, fontSize: 13, color: p.goals ? GOLD : MUTED, textAlign: 'center' }}>{p.goals || '—'}</span>
                  <span style={{ fontFamily: DISP, fontSize: 13, color: p.assists ? BLUE : MUTED, textAlign: 'center' }}>{p.assists || '—'}</span>
                  {!isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: p.yellows ? ORANGE : MUTED, textAlign: 'center' }}>{p.yellows || '—'}</span>}
                  {!isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: p.reds ? RED : MUTED, textAlign: 'center' }}>{p.reds || '—'}</span>}
                  {hasVideo && <span style={{ fontFamily: MONO, fontSize: 11, color: INK, textAlign: 'center' }}>{p.ps?.passes || '—'}</span>}
                  {hasVideo && !isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: INK, textAlign: 'center' }}>{p.ps?.shots || '—'}</span>}
                  {hasVideo && !isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: INK, textAlign: 'center' }}>{p.ps?.duels_won || '—'}/{p.ps?.duels || '—'}</span>}
                  {hasVideo && <span style={{ fontFamily: MONO, fontSize: 11, color: INK, textAlign: 'center' }}>{p.ps?.distance_km || '—'}</span>}
                </div>
              ))}

              {/* Remplaçants */}
              {rows.filter(p => !p.starter).length > 0 && (
                <>
                  <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, padding: '8px 20px', background: CREAM, borderTop: `1px solid ${RULE}` }}>Remplaçants</div>
                  {rows.filter(p => !p.starter).map((p, i) => (
                    <div key={p.player_id || i} style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '11px 20px', alignItems: 'center', borderBottom: `1px solid ${RULE}`, background: i % 2 === 0 ? '#fff' : 'rgba(245,242,235,0.4)', opacity: 0.75 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(26,25,22,0.05)', border: `1px solid ${RULE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontFamily: DISP, fontSize: 12, color: MUTED }}>{p.number}</span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          <div style={{ fontFamily: MONO, fontSize: 8, color: MUTED, letterSpacing: '.06em' }}>{p.subbed_in ? `↑${p.subbed_in}'` : 'RMP'}</div>
                        </div>
                      </div>
                      <span style={{ fontFamily: DISP, fontSize: 13, color: MUTED, textAlign: 'center' }}>{p.minutes > 0 ? `${p.minutes}'` : '—'}</span>
                      <span style={{ fontFamily: DISP, fontSize: 13, color: p.goals ? GOLD : MUTED, textAlign: 'center' }}>{p.goals || '—'}</span>
                      <span style={{ fontFamily: DISP, fontSize: 13, color: p.assists ? BLUE : MUTED, textAlign: 'center' }}>{p.assists || '—'}</span>
                      {!isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: p.yellows ? ORANGE : MUTED, textAlign: 'center' }}>{p.yellows || '—'}</span>}
                      {!isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: p.reds ? RED : MUTED, textAlign: 'center' }}>{p.reds || '—'}</span>}
                      {hasVideo && <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED, textAlign: 'center' }}>{p.ps?.passes || '—'}</span>}
                      {hasVideo && !isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED, textAlign: 'center' }}>{p.ps?.shots || '—'}</span>}
                      {hasVideo && !isMobile && <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED, textAlign: 'center' }}>{p.ps?.duels_won || '—'}/{p.ps?.duels || '—'}</span>}
                      {hasVideo && <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED, textAlign: 'center' }}>{p.ps?.distance_km || '—'}</span>}
                    </div>
                  ))}
                </>
              )}
            </Card>
          </div>
        )
      })()}

      {/* ── ANALYSE IA ── */}
      {ongletActif === 'insights' && match.ai_insights && (
        <Card accentColor={GOLD} style={{ padding: '32px', animation: 'fadeUp .3s ease' }}>
          <SectionTitle>Analyse IA</SectionTitle>
          <div style={{ padding: '20px', fontFamily: MONO, fontSize: 13, color: INK, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{match.ai_insights}</div>
        </Card>
      )}

      {/* ── RAPPORT PDF ── */}
      {ongletActif === 'report' && match.pdf_url && (
        <Card accentColor={GOLD} style={{ padding: '48px 24px', textAlign: 'center', animation: 'fadeUp .3s ease' }}>
          <Download size={28} color={GOLD} style={{ marginBottom: 16 }} />
          <div style={{ fontFamily: DISP, fontSize: 22, textTransform: 'uppercase', color: INK, marginBottom: 8 }}>Rapport disponible</div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginBottom: 24 }}>Téléchargez le rapport complet de l'analyse de ce match.</div>
          <button onClick={handleDownloadPDF} style={{ padding: '14px 32px', background: GOLD, color: D, fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
            Télécharger le PDF
          </button>
        </Card>
      )}

      {/* ── Zone danger ── */}
      <div style={{ marginTop: 48, paddingTop: 20, borderTop: `1px solid ${RULE}`, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setShowDeleteMatchModal(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(239,68,68,0.4)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, transition: 'color .15s' }}
          onMouseEnter={e => e.currentTarget.style.color = RED}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(239,68,68,0.4)'}
        >
          <Trash2 size={10} /> Supprimer ce match
        </button>
      </div>

      {/* Modal suppression match */}
      {showDeleteMatchModal && (
        <div
          onClick={() => { if (!deleteMatchLoading) { setShowDeleteMatchModal(false); setDeleteMatchConfirm('') } }}
          style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: T.surface, border: `1px solid ${RULE}`, borderTop: `2px solid ${RED}`, borderRadius: 10, maxWidth: 460, width: '100%', padding: 28, boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <AlertCircle size={18} color={RED} />
              <span style={{ fontFamily: DISP, fontSize: 18, fontWeight: 700, color: INK, textTransform: 'uppercase', letterSpacing: '.02em' }}>Supprimer ce match ?</span>
            </div>
            <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.05)', border: `1px solid rgba(239,68,68,0.15)`, marginBottom: 20, borderRadius: 4 }}>
              <p style={{ fontFamily: MONO, fontSize: 11, color: MUTED, lineHeight: 1.7, margin: 0 }}>
                Cette action est irréversible. Toutes les données du match seront perdues : événements, composition, vidéo, statistiques des joueurs liées à ce match. Si des cartes joueurs ont déjà été partagées pour ce match, leur lien deviendra invalide.
              </p>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>
                Tape le nom de l'adversaire <strong style={{ color: RED }}>{match.opponent}</strong> pour confirmer
              </div>
              <input
                type="text"
                autoFocus
                value={deleteMatchConfirm}
                onChange={e => setDeleteMatchConfirm(e.target.value)}
                placeholder={match.opponent}
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', border: `1px solid ${deleteMatchConfirm === match.opponent ? RED : RULE}`, borderRadius: 4, background: T.bg, color: INK, fontFamily: MONO, fontSize: 13, outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setShowDeleteMatchModal(false); setDeleteMatchConfirm('') }}
                disabled={deleteMatchLoading}
                style={{ flex: 1, padding: '11px', background: 'transparent', border: `1px solid ${RULE}`, fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED, cursor: deleteMatchLoading ? 'default' : 'pointer', borderRadius: 4 }}
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteMatch}
                disabled={deleteMatchConfirm !== match.opponent || deleteMatchLoading}
                style={{ flex: 2, padding: '11px', background: deleteMatchConfirm === match.opponent ? RED : 'rgba(239,68,68,0.15)', border: 'none', borderRadius: 4, fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, color: deleteMatchConfirm === match.opponent ? '#fff' : 'rgba(239,68,68,0.4)', cursor: deleteMatchConfirm === match.opponent && !deleteMatchLoading ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {deleteMatchLoading ? 'Suppression…' : <><Trash2 size={11} /> Supprimer définitivement</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PIED DE PAGE D'IMPRESSION ────────────────────────────────────
          Invisible a l'ecran, repete sur CHAQUE page imprimee (position
          fixed, cf. printStyles). Il porte ce qu'un document detache de son
          contexte doit dire de lui-meme · quand il a ete produit, pour qui,
          et sur quelle version de formule reposent ses indicateurs. */}
      <div data-print="footer" style={{ display: 'none' }}>
        {[
          new Date().toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          user?.club_name || null,
          versionFormule != null ? `Indicateurs v${versionFormule}` : null,
        ].filter(Boolean).join('  ·  ')}
      </div>

    </DashboardLayout>
  )
}
